import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../hooks/useDebounce';
import { useSmartRefresh } from '../hooks/useSmartRefresh';
import { Shield, Building, Users, UserCheck, Calendar, Baby, Plus, Edit, Trash2, LogOut, BarChart3, Search, Upload, Download, RefreshCw, User, Settings, ChevronDown, ChevronRight, HeartPulse } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { PhoneInput } from './ui/phone-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateInput } from './ui/date-input';
import MedicationIcon from './MedicationIcon';
import SecurityAuditTab from './SecurityAuditTab';
import axios from 'axios';
import { toast } from 'sonner';
import { formatLeaveDates } from '../lib/utils';
import { formatDateToDDMMYYYY } from '../utils/dateFormatter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Super Specialization options based on main specialization
const SUPER_SPECIALIZATION_OPTIONS = {
  'Pediatrician': ['Neonatology', 'Pediatric Cardiology', 'Pediatric Neurology', 'Pediatric Oncology', 'Pediatric Pulmonology', 'Pediatric Gastroenterology', 'Pediatric Endocrinology', 'Pediatric Nephrology', 'Pediatric Rheumatology', 'Developmental Pediatrics'],
  'General Medicine': ['Internal Medicine', 'Critical Care Medicine', 'Infectious Disease', 'Geriatric Medicine', 'Palliative Medicine'],
  'General Surgeon': ['Laparoscopic Surgery', 'Trauma Surgery', 'Surgical Oncology', 'Vascular Surgery', 'Colorectal Surgery'],
  'Neurosurgeon': ['Spine Surgery', 'Pediatric Neurosurgery', 'Neuro-oncology', 'Functional Neurosurgery', 'Cerebrovascular Surgery'],
  'Cardiologist': ['Interventional Cardiology', 'Electrophysiology', 'Heart Failure', 'Preventive Cardiology', 'Cardiac Imaging'],
  'Neurologist': ['Epilepsy', 'Movement Disorders', 'Stroke', 'Neuromuscular Disease', 'Headache Medicine', 'Neuro-immunology'],
  'Pulmonologist': ['Critical Care', 'Sleep Medicine', 'Interventional Pulmonology', 'Pulmonary Hypertension'],
  'Gastroenterologist': ['Hepatology', 'IBD', 'Pancreatic Disease', 'GI Oncology', 'Motility Disorders'],
  'Endocrinologist': ['Diabetes', 'Thyroid Disorders', 'Reproductive Endocrinology', 'Bone & Mineral Disorders'],
  'Dermatologist': ['Cosmetic Dermatology', 'Pediatric Dermatology', 'Dermatopathology', 'Mohs Surgery'],
  'ENT Specialist': ['Otology', 'Rhinology', 'Laryngology', 'Head & Neck Surgery', 'Pediatric ENT'],
  'Dentist': ['Orthodontics', 'Periodontics', 'Endodontics', 'Prosthodontics', 'Oral Surgery', 'Pediatric Dentistry'],
  'Psychiatrist': ['Child & Adolescent Psychiatry', 'Addiction Psychiatry', 'Geriatric Psychiatry', 'Forensic Psychiatry'],
  'Obstetrician/Gynec': ['Maternal-Fetal Medicine', 'Reproductive Endocrinology', 'Gynecologic Oncology', 'Urogynecology'],
  'Oncologist': ['Medical Oncology', 'Radiation Oncology', 'Surgical Oncology', 'Hematology-Oncology', 'Pediatric Oncology'],
  'Urologist': ['Pediatric Urology', 'Urologic Oncology', 'Female Urology', 'Andrology', 'Renal Transplant'],
  'Physiotherapy': ['Sports Physiotherapy', 'Neurological Physiotherapy', 'Pediatric Physiotherapy', 'Cardiopulmonary Physiotherapy', 'Orthopedic Physiotherapy']
};

const CentralAdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [hospitals, setHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [frontDeskStaff, setFrontDeskStaff] = useState([]);
  const [medicalAssistants, setMedicalAssistants] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [babies, setBabies] = useState([]);
  const [babyStats, setBabyStats] = useState({
    total_babies: 0,
    created_via_baby_app: 0,
    created_via_appointment: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Database Export/Import state
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [clearBeforeImport, setClearBeforeImport] = useState(false);
  
  // Common Medications state
  const [commonMedications, setCommonMedications] = useState([]);
  const [loadingMedications, setLoadingMedications] = useState(false);
  const [selectedMedicationLetter, setSelectedMedicationLetter] = useState('');
  const [medicationSearchQuery, setMedicationSearchQuery] = useState('');
  const debouncedMedicationSearch = useDebounce(medicationSearchQuery, 300);
  const [showDeleteMedicationDialog, setShowDeleteMedicationDialog] = useState(false);
  const [showDeleteMedicationConfirmDialog, setShowDeleteMedicationConfirmDialog] = useState(false);
  const [deleteMedicationConfirmText, setDeleteMedicationConfirmText] = useState('');
  const [medicationToDelete, setMedicationToDelete] = useState(null);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [selectedMedications, setSelectedMedications] = useState(new Set());
  const [showBulkDeleteMedicationDialog, setShowBulkDeleteMedicationDialog] = useState(false);
  const [showBulkDeleteMedicationConfirmDialog, setShowBulkDeleteMedicationConfirmDialog] = useState(false);
  const [bulkDeleteMedicationConfirmText, setBulkDeleteMedicationConfirmText] = useState('');
  
  // Add Medication state
  const [showAddMedicationDialog, setShowAddMedicationDialog] = useState(false);
  const [medicationForm, setMedicationForm] = useState({
    medication_name: '',
    medication_type: '',
    generic_name: '',
    manufacturer: '',
    treatment_for: '',
    category: '',
    time_of_day: [],
    frequency: '',
    timing: 'Before Food',
    duration: '',
    notes: ''
  });
  
  // Common Tests state
  const [commonTests, setCommonTests] = useState([]);
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [showAddTestDialog, setShowAddTestDialog] = useState(false);
  const [showDeleteTestDialog, setShowDeleteTestDialog] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  const [testForm, setTestForm] = useState({
    test_name: '',
    test_type: 'Blood Test',
    category: '',
    description: ''
  });
  const [expandedTestTypes, setExpandedTestTypes] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});

  // Baby App Settings state
  const [babyAppSettings, setBabyAppSettings] = useState({
    login_enabled: true,
    profile_creation_enabled: true,
    join_sync_enabled: true
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  
  // AI Features Master Settings state
  const [aiFeatureSettings, setAiFeatureSettings] = useState({
    ai_clinical_assistant_enabled: true,
    differential_diagnosis_enabled: true,
    voice_dictation_enabled: true
  });
  const [loadingAiSettings, setLoadingAiSettings] = useState(false);

  // Helper function to normalize gender display
  const normalizeGender = (gender) => {
    if (!gender) return 'Unknown';
    const lowerGender = gender.toLowerCase();
    if (lowerGender === 'boy' || lowerGender === 'male' || lowerGender === 'm') return 'Male';
    if (lowerGender === 'girl' || lowerGender === 'female' || lowerGender === 'f') return 'Female';
    return gender;
  };
  const [refreshing, setRefreshing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showAddHospitalDialog, setShowAddHospitalDialog] = useState(false);
  const [showEditHospitalDialog, setShowEditHospitalDialog] = useState(false);
  const [editingHospital, setEditingHospital] = useState(null);
  const [expandedHospital, setExpandedHospital] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAppointmentYear, setSelectedAppointmentYear] = useState(new Date().getFullYear());
  const [showAddDoctorDialog, setShowAddDoctorDialog] = useState(false);
  const [showAddStaffDialog, setShowAddStaffDialog] = useState(false);
  const [showEditDoctorDialog, setShowEditDoctorDialog] = useState(false);
  const [showEditStaffDialog, setShowEditStaffDialog] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [selectedHospitalForAdd, setSelectedHospitalForAdd] = useState(null);
  const [selectedHospitalsForDoctor, setSelectedHospitalsForDoctor] = useState([]); // Multiple hospitals for doctor
  const [hospitalSearchQuery, setHospitalSearchQuery] = useState(''); // Search for hospitals in doctor assignment
  const [doctorForm, setDoctorForm] = useState({
    name: '',
    specialization: '',
    super_specialization: '',
    registered_number: '',
    contact: '',
    password: '',
    photo_url: '',
    country_code: '+91'
  });
  const [searchedDoctor, setSearchedDoctor] = useState(null); // Stores found doctor from search
  const [searchingDoctor, setSearchingDoctor] = useState(false); // Loading state for search
  const [isExistingDoctor, setIsExistingDoctor] = useState(false); // Flag if doctor exists
  const [staffForm, setStaffForm] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    country_code: '+91'
  });
  
  // Medical Assistant state
  const [showAddMADialog, setShowAddMADialog] = useState(false);
  const [editingMA, setEditingMA] = useState(null);
  const [maForm, setMAForm] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    doctor_ids: [],
    country_code: '+91'
  });
  
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [hospitalForm, setHospitalForm] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    contact_email: '',
    contact_phone: '',
    password: '',
    primary_color: '#3B82F6',
    secondary_color: '#8B5CF6',
    country_code: '+91'
  });
  
  // Baby edit/delete state
  const [showEditBabyDialog, setShowEditBabyDialog] = useState(false);
  const [showDeleteBabyDialog, setShowDeleteBabyDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [editingBaby, setEditingBaby] = useState(null);
  const [deletingBaby, setDeletingBaby] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Doctor delete state
  const [showDeleteDoctorDialog, setShowDeleteDoctorDialog] = useState(false);
  const [showDeleteDoctorConfirmDialog, setShowDeleteDoctorConfirmDialog] = useState(false);
  const [deletingDoctor, setDeletingDoctor] = useState(null);
  const [deleteDoctorConfirmText, setDeleteDoctorConfirmText] = useState('');
  const [babyForm, setBabyForm] = useState({
    name: '',
    date_of_birth: ''
  });
  
  // Bulk delete state
  const [selectedBabies, setSelectedBabies] = useState(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showBulkDeleteConfirmDialog, setShowBulkDeleteConfirmDialog] = useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');

  // Filtered babies based on search query
  const filteredBabies = useMemo(() => {
    return babies.filter(baby => 
      baby.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      baby.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (baby.short_id && baby.short_id.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (baby.parent_phone && baby.parent_phone.includes(searchQuery))
    );
  }, [babies, searchQuery]);

  // Filtered medications based on search query (debounced for performance)
  const filteredMedications = useMemo(() => {
    return commonMedications.filter(med => 
      med.medication_name.toLowerCase().includes(debouncedMedicationSearch.toLowerCase()) ||
      (med.generic_name && med.generic_name.toLowerCase().includes(debouncedMedicationSearch.toLowerCase())) ||
      (med.category && med.category.toLowerCase().includes(debouncedMedicationSearch.toLowerCase()))
    );
  }, [commonMedications, debouncedMedicationSearch]);

  useEffect(() => {
    // Check if admin is logged in
    const adminToken = localStorage.getItem('centralAdminToken');
    if (!adminToken) {
      navigate('/central-admin-login');
      return;
    }
    fetchData();
  }, [navigate]);

  // Real-time data synchronization function
  const syncData = useCallback(async () => {
    // Check if user is actively working (dialog open, input focused, or typing)
    const activeElement = document.activeElement;
    const isInputActive = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    );
    
    // Check if any dialog/modal is open
    const isDialogOpen = document.querySelector('[role="dialog"]') !== null;
    
    // Skip sync if user is actively working
    if (isInputActive || isDialogOpen) {
      console.log('⏸️ Skipping sync - user is actively working');
      return;
    }

    try {
      // Use Promise.allSettled to handle partial failures
      const results = await Promise.allSettled([
        axios.get(`${API}/hospitals`),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/front-desk-staff`)
      ]);
      
      const [hospitalsRes, doctorsRes, staffRes] = results;
      
      // Only update if request succeeded and data has changed
      if (hospitalsRes.status === 'fulfilled' && JSON.stringify(hospitalsRes.value.data) !== JSON.stringify(hospitals)) {
        setHospitals(hospitalsRes.value.data);
        console.log('🔄 Hospitals updated in real-time');
      }

      if (doctorsRes.status === 'fulfilled' && JSON.stringify(doctorsRes.value.data) !== JSON.stringify(doctors)) {
        setDoctors(doctorsRes.value.data);
        console.log('🔄 Doctors updated in real-time');
      }

      if (staffRes.status === 'fulfilled' && JSON.stringify(staffRes.value.data) !== JSON.stringify(frontDeskStaff)) {
        setFrontDeskStaff(staffRes.value.data);
        console.log('🔄 Front desk staff updated in real-time');
      }

      // Update last sync time after sync attempt
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Error syncing data:', error);
      // Silently fail - don't show error to user for background sync
    }
  }, [hospitals, doctors, frontDeskStaff]);

  // Smart auto-refresh that pauses during user interactions
  useSmartRefresh(
    syncData,
    30000, // 30 seconds
    3000   // Wait 3 seconds after user activity before resuming
  );

  useEffect(() => {
    // Clear search when changing tabs
    setSearchQuery('');
  }, [activeTab]);

  useEffect(() => {
    // Load common medications and tests when on configuration tab
    if (activeTab === 'configuration') {
      loadCommonMedications();
      loadCommonTests();
    }
    // Load baby app settings and AI features settings when on settings tab
    if (activeTab === 'settings') {
      loadBabyAppSettings();
      loadAiFeatureSettings();
    }
  }, [activeTab]);


  const fetchData = async (showToast = false, retryCount = 0) => {
    const MAX_RETRIES = 2;
    
    try {
      if (showToast) setRefreshing(true);
      else setLoading(true);
      
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        axios.get(`${API}/central-admin/dashboard-stats`),
        axios.get(`${API}/hospitals`),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/front-desk-staff`),
        axios.get(`${API}/appointments`),
        axios.get(`${API}/babies`),
        axios.get(`${API}/babies/creation-stats`),
        axios.get(`${API}/medical-assistants`)
      ]);

      const [statsRes, hospitalsRes, doctorsRes, staffRes, appointmentsRes, babiesRes, babyStatsRes, maRes] = results;

      // Track if any requests failed
      let hasFailures = false;

      // Update state for successful requests only
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      } else {
        console.error('Failed to fetch stats:', statsRes.reason);
        hasFailures = true;
      }

      if (hospitalsRes.status === 'fulfilled') {
        setHospitals(hospitalsRes.value.data);
      } else {
        console.error('Failed to fetch hospitals:', hospitalsRes.reason);
        hasFailures = true;
      }

      if (doctorsRes.status === 'fulfilled') {
        setDoctors(doctorsRes.value.data);
      } else {
        console.error('Failed to fetch doctors:', doctorsRes.reason);
        hasFailures = true;
      }

      if (staffRes.status === 'fulfilled') {
        setFrontDeskStaff(staffRes.value.data);
      } else {
        console.error('Failed to fetch front desk staff:', staffRes.reason);
        hasFailures = true;
      }

      if (maRes.status === 'fulfilled') {
        setMedicalAssistants(maRes.value.data);
      } else {
        console.error('Failed to fetch medical assistants:', maRes.reason);
        hasFailures = true;
      }

      if (appointmentsRes.status === 'fulfilled') {
        setAppointments(appointmentsRes.value.data);
      } else {
        console.error('Failed to fetch appointments:', appointmentsRes.reason);
        hasFailures = true;
      }

      if (babiesRes.status === 'fulfilled') {
        setBabies(babiesRes.value.data);
      } else {
        console.error('Failed to fetch babies:', babiesRes.reason);
        hasFailures = true;
      }

      if (babyStatsRes.status === 'fulfilled') {
        setBabyStats(babyStatsRes.value.data);
      } else {
        console.error('Failed to fetch baby stats:', babyStatsRes.reason);
        hasFailures = true;
      }

      setLastSyncTime(new Date());
      
      // If there were failures and we haven't exceeded retry limit, retry
      if (hasFailures && retryCount < MAX_RETRIES) {
        console.log(`Retrying failed requests (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        setTimeout(() => fetchData(false, retryCount + 1), 1000);
      }
      
      if (showToast) {
        if (hasFailures) {
          toast.warning('Some data may not have loaded. Please refresh if needed.');
        } else {
          toast.success('Data refreshed successfully');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      
      // Retry on complete failure
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying after error (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        setTimeout(() => fetchData(showToast, retryCount + 1), 1000);
        return;
      }
      
      if (showToast) {
        toast.error('Failed to refresh data. Please try again.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('centralAdmin');
    localStorage.removeItem('centralAdminToken');
    navigate('/central-admin-login');
    toast.success('Logged out successfully');
  };

  // Database Export function
  const handleExportDatabase = async () => {
    try {
      setExporting(true);
      toast.info('Exporting database... This may take a moment.');
      
      const response = await axios.get(`${API}/central-admin/export-database`);
      const exportData = response.data;
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `database_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Database exported successfully! ${exportData.total_documents} documents across ${exportData.total_collections} collections.`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export database: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  // Database Import function
  const handleImportDatabase = async () => {
    if (!importFile) {
      toast.error('Please select a backup file to import');
      return;
    }
    
    try {
      setImporting(true);
      toast.info('Importing database... This may take a moment.');
      
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('clear_existing', clearBeforeImport);
      
      const response = await axios.post(`${API}/central-admin/import-database-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      setShowImportDialog(false);
      setImportFile(null);
      setClearBeforeImport(false);
      
      // Refresh data
      window.location.reload();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import database: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // Load baby app settings
  const loadBabyAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/baby-app-settings`);
      setBabyAppSettings(response.data);
    } catch (error) {
      console.error('Error loading baby app settings:', error);
      toast.error('Failed to load baby app settings');
    }
  };

  // Update baby app settings
  const handleUpdateBabyAppSettings = async (field, value) => {
    try {
      setLoadingSettings(true);
      const updatedSettings = { ...babyAppSettings, [field]: value };
      await axios.put(`${API}/baby-app-settings`, { [field]: value });
      setBabyAppSettings(updatedSettings);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating baby app settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setLoadingSettings(false);
    }
  };

  // Load AI features master settings
  const loadAiFeatureSettings = async () => {
    try {
      const response = await axios.get(`${API}/ai-features-settings`);
      setAiFeatureSettings(response.data);
    } catch (error) {
      console.error('Error loading AI features settings:', error);
    }
  };

  // Update AI features master settings
  const handleUpdateAiFeatureSettings = async (field, value) => {
    try {
      setLoadingAiSettings(true);
      const updatedSettings = { ...aiFeatureSettings, [field]: value };
      await axios.put(`${API}/ai-features-settings`, { [field]: value });
      setAiFeatureSettings(updatedSettings);
      toast.success('AI features settings updated');
    } catch (error) {
      console.error('Error updating AI features settings:', error);
      toast.error('Failed to update AI settings');
    } finally {
      setLoadingAiSettings(false);
    }
  };

  // Update hospital AI features toggle
  const handleUpdateHospitalAiFeatures = async (hospitalId, enabled) => {
    try {
      await axios.put(`${API}/hospitals/${hospitalId}/ai-features`, { ai_features_enabled: enabled });
      // Update local state
      setHospitals(prev => prev.map(h => 
        h.id === hospitalId ? { ...h, ai_features_enabled: enabled } : h
      ));
      toast.success(`AI features ${enabled ? 'enabled' : 'disabled'} for hospital`);
    } catch (error) {
      console.error('Error updating hospital AI features:', error);
      toast.error('Failed to update hospital AI features');
    }
  };

  // Load common medications
  const loadCommonMedications = async (letter = '') => {
    setLoadingMedications(true);
    try {
      // If letter is provided, filter by letter; otherwise load all
      const params = letter ? { letter } : {};
      const response = await axios.get(`${API}/common-medications`, { params });
      setCommonMedications(response.data);
      setSelectedMedicationLetter(letter);
    } catch (error) {
      console.error('Error loading common medications:', error);
      toast.error('Failed to load common medications');
    } finally {
      setLoadingMedications(false);
    }
  };

  // Delete common medication
  const handleDeleteMedicationFirstStep = () => {
    setShowDeleteMedicationDialog(false);
    setShowDeleteMedicationConfirmDialog(true);
    setDeleteMedicationConfirmText('');
  };

  const handleDeleteMedicationConfirm = async () => {
    if (deleteMedicationConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!medicationToDelete) return;
    
    try {
      await axios.delete(`${API}/common-medications/${medicationToDelete.id}`);
      toast.success('Medication deleted successfully');
      setShowDeleteMedicationConfirmDialog(false);
      setMedicationToDelete(null);
      setDeleteMedicationConfirmText('');
      loadCommonMedications(); // Reload the list
    } catch (error) {
      console.error('Error deleting medication:', error);
      toast.error('Failed to delete medication');
      setShowDeleteMedicationConfirmDialog(false);
      setMedicationToDelete(null);
      setDeleteMedicationConfirmText('');
    }
  };

  const handleCancelDeleteMedication = () => {
    setShowDeleteMedicationDialog(false);
    setShowDeleteMedicationConfirmDialog(false);
    setMedicationToDelete(null);
    setDeleteMedicationConfirmText('');
  };

  // Bulk import medications
  const handleBulkImportCSV = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploadingCSV(true);
    
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      // Show loading toast
      const loadingToast = toast.loading('Uploading and processing CSV file...');

      const response = await axios.post(`${API}/common-medications/bulk-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      const { inserted_count, skipped_count, error_count, message } = response.data;
      
      // Show detailed success message
      if (inserted_count > 0) {
        toast.success(message || `✅ Imported ${inserted_count} medications successfully`);
      } else if (skipped_count > 0 && inserted_count === 0) {
        toast.info(`ℹ️ All ${skipped_count} medications already exist (skipped duplicates)`);
      } else if (error_count > 0) {
        toast.warning(message || '⚠️ Import completed with errors');
      } else {
        toast.info('ℹ️ No medications were imported');
      }
      
      setShowBulkImportDialog(false);
      setCsvFile(null);
      await loadCommonMedications(); // Reload the list
    } catch (error) {
      console.error('Error importing common medications:', error);
      toast.error(error.response?.data?.detail || '❌ Failed to import common medications');
    } finally {
      setUploadingCSV(false);
    }
  };

  // Add Common Medication
  const handleAddCommonMedication = async (e) => {
    e.preventDefault();
    
    try {
      const medicationData = {
        medication_name: medicationForm.medication_name,
        medication_type: medicationForm.medication_type || null,
        generic_name: medicationForm.generic_name, // Now required
        manufacturer: medicationForm.manufacturer || null,
        category: medicationForm.category || null,
        treatment_for: medicationForm.treatment_for || null,
        time_of_day: medicationForm.time_of_day,
        frequency: medicationForm.frequency || null,
        default_timing: medicationForm.timing,
        default_duration: medicationForm.duration || null,
        notes: medicationForm.notes || null
      };

      await axios.post(`${API}/common-medications`, medicationData);
      toast.success('Common medication added successfully!');
      
      // Reset form and close dialog
      setMedicationForm({
        medication_name: '',
        medication_type: '',
        generic_name: '',
        manufacturer: '',
        category: '',
        treatment_for: '',
        time_of_day: [],
        frequency: '',
        timing: 'Before Food',
        duration: '',
        notes: ''
      });
      setShowAddMedicationDialog(false);
      loadCommonMedications(); // Reload the list
    } catch (error) {
      console.error('Error adding common medication:', error);
      toast.error(error.response?.data?.detail || 'Failed to add common medication');
    }
  };

  // Toggle time of day for medication form
  const toggleTimeOfDay = (time) => {
    setMedicationForm(prev => ({
      ...prev,
      time_of_day: prev.time_of_day.includes(time)
        ? prev.time_of_day.filter(t => t !== time)
        : [...prev.time_of_day, time]
    }));
  };

  // Load common tests
  const loadCommonTests = async () => {
    try {
      const response = await axios.get(`${API}/common-tests`);
      setCommonTests(response.data);
    } catch (error) {
      console.error('Error loading common tests:', error);
      toast.error('Failed to load common tests');
    }
  };

  // Add common test
  const handleAddTest = async (e) => {
    e.preventDefault();
    
    if (!testForm.test_name || !testForm.test_type) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Validate category for Blood Test
    if (testForm.test_type === 'Blood Test' && !testForm.category) {
      toast.error('Please select a category for Blood Test');
      return;
    }
    
    try {
      await axios.post(`${API}/common-tests`, {
        test_name: testForm.test_name,
        test_type: testForm.test_type,
        category: testForm.test_type === 'Blood Test' ? testForm.category : null,
        description: testForm.description || null
      });
      
      toast.success('Test added successfully');
      setShowAddTestDialog(false);
      setTestForm({
        test_name: '',
        test_type: 'Blood Test',
        category: '',
        description: ''
      });
      loadCommonTests(); // Reload the list
    } catch (error) {
      console.error('Error adding test:', error);
      toast.error('Failed to add test');
    }
  };

  // Delete common test
  const handleDeleteTest = async () => {
    if (!testToDelete) return;
    
    try {
      await axios.delete(`${API}/common-tests/${testToDelete.id}`);
      toast.success('Test deleted successfully');
      setShowDeleteTestDialog(false);
      setTestToDelete(null);
      loadCommonTests(); // Reload the list
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test');
    }
  };


  // Generate Hospital ID: [2 state code][3 city letters][3 numbers]
  const generateHospitalId = (stateCode, city) => {
    const cityCode = city.substring(0, 3).toUpperCase();
    const randomNum = Math.floor(100 + Math.random() * 900); // 3 digit number
    return `${stateCode}${cityCode}${randomNum}`;
  };

  // Generate Hospital ID: Same as code
  const generateHospitalCode = (stateCode, city) => {
    return generateHospitalId(stateCode, city);
  };

  const handleAddHospital = async (e) => {
    e.preventDefault();
    try {
      // Validation
      if (!hospitalForm.name || !hospitalForm.address_line1 || !hospitalForm.city || 
          !hospitalForm.state || !hospitalForm.pincode || !hospitalForm.password) {
        toast.error('Please fill all required fields (Name, Address Line 1, City, State, Pincode, Password)');
        return;
      }

      // Validate city has at least 3 letters (needed for hospital ID generation: STATE(2) + CITY(3) + RANDOM(3))
      if (hospitalForm.city.length < 3) {
        toast.error('City name must have at least 3 letters (used for Hospital ID generation)');
        return;
      }

      // Validate pincode is exactly 6 digits
      if (hospitalForm.pincode.length !== 6) {
        toast.error('Pincode must be exactly 6 digits');
        return;
      }

      // Generate hospital ID and code using state and city
      const hospitalId = generateHospitalId(hospitalForm.state, hospitalForm.city);
      const hospitalCode = generateHospitalCode(hospitalForm.state, hospitalForm.city);
      
      // Check if hospital with this ID already exists
      const existingHospitals = await axios.get(`${API}/hospitals`);
      if (existingHospitals.data.find(h => h.id === hospitalId)) {
        toast.error('A hospital with similar location already exists. Please use a different city or state.');
        return;
      }

      // Format address
      const addressLines = [hospitalForm.address_line1];
      if (hospitalForm.address_line2) {
        addressLines.push(hospitalForm.address_line2);
      }
      addressLines.push(`${hospitalForm.city}, ${hospitalForm.state} ${hospitalForm.pincode}`);
      const fullAddress = addressLines.join('\n');

      const hospitalData = {
        id: hospitalId,
        name: hospitalForm.name,
        code: hospitalCode,
        password: hospitalForm.password,
        address: fullAddress,
        contact_email: hospitalForm.contact_email || '',
        contact_phone: hospitalForm.contact_phone || '',
        primary_color: hospitalForm.primary_color || '#3B82F6',
        secondary_color: hospitalForm.secondary_color || '#8B5CF6'
      };

      await axios.post(`${API}/hospitals`, hospitalData);
      toast.success(`Hospital added successfully with ID: ${hospitalId}`);
      setShowAddHospitalDialog(false);
      setHospitalForm({
        name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pincode: '',
        contact_email: '',
        contact_phone: '',
        password: '',
        primary_color: '#3B82F6',
        secondary_color: '#8B5CF6',
        country_code: '+91'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add hospital');
    }
  };

  const handleEditHospital = (hospital) => {
    setEditingHospital(hospital);
    
    // Parse address back to components
    const addressLines = hospital.address ? hospital.address.split('\n') : ['', '', ''];
    const address_line1 = addressLines[0] || '';
    const address_line2 = addressLines.length > 2 ? addressLines[1] : '';
    
    // Parse city, state, pincode from last line (format: "City, State Pincode")
    const lastLine = addressLines[addressLines.length - 1] || '';
    const parts = lastLine.split(',');
    const city = parts[0] || '';
    const stateAndPin = parts[1] ? parts[1].trim().split(' ') : ['', ''];
    const state = stateAndPin[0] || '';
    const pincode = stateAndPin[1] || '';
    
    setHospitalForm({
      name: hospital.name,
      address_line1,
      address_line2,
      city,
      state,
      pincode,
      contact_email: hospital.contact_email || '',
      contact_phone: hospital.contact_phone || '',
      primary_color: hospital.primary_color || '#3B82F6',
      secondary_color: hospital.secondary_color || '#8B5CF6'
    });
    setShowEditHospitalDialog(true);
  };

  const handleUpdateHospital = async (e) => {
    e.preventDefault();
    try {
      // Validation
      if (!hospitalForm.name || !hospitalForm.address_line1 || !hospitalForm.city || 
          !hospitalForm.state || !hospitalForm.pincode || !hospitalForm.password) {
        toast.error('Please fill all required fields (Name, Address Line 1, City, State, Pincode, Password)');
        return;
      }

      // Validate city has at least 3 letters
      if (hospitalForm.city.length < 3) {
        toast.error('City name must have at least 3 letters');
        return;
      }

      // Validate pincode is exactly 6 digits
      if (hospitalForm.pincode.length !== 6) {
        toast.error('Pincode must be exactly 6 digits');
        return;
      }

      // Format address
      const addressLines = [hospitalForm.address_line1];
      if (hospitalForm.address_line2) {
        addressLines.push(hospitalForm.address_line2);
      }
      addressLines.push(`${hospitalForm.city}, ${hospitalForm.state} ${hospitalForm.pincode}`);
      const fullAddress = addressLines.join('\n');

      const hospitalData = {
        name: hospitalForm.name,
        code: editingHospital.code, // Keep existing code
        password: editingHospital.password, // CRITICAL FIX: Preserve password
        address: fullAddress,
        contact_email: hospitalForm.contact_email || '',
        contact_phone: hospitalForm.contact_phone || '',
        primary_color: hospitalForm.primary_color || '#3B82F6',
        secondary_color: hospitalForm.secondary_color || '#8B5CF6'
      };

      await axios.put(`${API}/hospitals/${editingHospital.id}`, hospitalData);
      toast.success('Hospital updated successfully');
      setShowEditHospitalDialog(false);
      setEditingHospital(null);
      setHospitalForm({
        name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pincode: '',
        contact_email: '',
        contact_phone: '',
        password: '',
        primary_color: '#3B82F6',
        secondary_color: '#8B5CF6',
        country_code: '+91'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update hospital');
    }
  };

  const handleDeleteHospital = async (hospitalId) => {
    if (!window.confirm('Are you sure you want to delete this hospital? It will be moved to deleted records and can be restored later.')) return;
    
    const admin = JSON.parse(localStorage.getItem('centralAdmin') || '{}');
    
    try {
      await axios.delete(`${API}/hospitals/${hospitalId}`, {
        params: {
          deleted_by_id: admin.username || 'admin',
          deleted_by_name: admin.name || 'Central Admin',
          deleted_by_role: 'central_admin'
        }
      });
      toast.success('Hospital deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete hospital');
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhoto) return '';

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedPhoto);

      const response = await axios.post(`${API}/upload-image/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return response.data.image_url;
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
      return '';
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Search for existing doctor by registration number
  const handleSearchDoctor = async () => {
    if (!doctorForm.registered_number || doctorForm.registered_number.trim() === '') {
      toast.error('Please enter registration number to search');
      return;
    }

    setSearchingDoctor(true);
    try {
      const response = await axios.get(`${API}/doctors/search-by-registration`, {
        params: { registration_number: doctorForm.registered_number.trim() }
      });
      
      // Doctor found - populate form with existing data
      const doctor = response.data;
      setSearchedDoctor(doctor);
      setIsExistingDoctor(true);
      setDoctorForm({
        name: doctor.name,
        specialization: doctor.specialization,
        super_specialization: doctor.super_specialization || '',
        registered_number: doctorForm.registered_number,
        contact: doctor.contact || '',
        photo_url: doctor.photo_url || '',
        country_code: '+91'
      });
      toast.success('Doctor found! Review details and click Add to assign to hospital.');
    } catch (error) {
      if (error.response?.status === 404) {
        // Doctor not found - allow new entry
        setSearchedDoctor(null);
        setIsExistingDoctor(false);
        toast.info('No existing doctor found. Please fill in the details to create new doctor.');
      } else {
        console.error('Search error:', error);
        toast.error('Failed to search for doctor');
      }
    } finally {
      setSearchingDoctor(false);
    }
  };

  // Generate Doctor ID: DOC + [registered number]
  const generateDoctorId = (registeredNumber) => {
    return `DOC${registeredNumber}`;
  };

  // Generate Staff ID: FD + [last 3 digits of hospital code] + [3 random digits]
  const generateStaffId = (hospitalCode) => {
    // Extract last 3 digits from hospital code (e.g., KABAN456 -> 456)
    const last3Digits = hospitalCode.match(/\d{3}$/)?.[0] || '000';
    const randomNum = Math.floor(100 + Math.random() * 900);
    return `FD${last3Digits}${randomNum}`;
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!doctorForm.name || !doctorForm.specialization || !doctorForm.registered_number || !doctorForm.contact || !doctorForm.password) {
      toast.error('Name, Specialization, Registered Number, Contact, and Password are required');
      return;
    }

    try {
      // Upload photo if selected
      let photoUrl = doctorForm.photo_url;
      if (selectedPhoto) {
        photoUrl = await handlePhotoUpload();
      }

      // Generate doctor ID
      const doctorId = generateDoctorId(doctorForm.registered_number);

      // CRITICAL FIX: Always include main hospital + additional hospitals
      const hospitalIds = [
        selectedHospitalForAdd.id, // Main hospital
        ...selectedHospitalsForDoctor.map(h => h.id) // Additional hospitals
      ];

      const doctorData = {
        id: doctorId,
        registration_number: doctorForm.registered_number,
        name: doctorForm.name,
        specialization: doctorForm.specialization,
        super_specialization: doctorForm.super_specialization || null,
        contact: doctorForm.contact,
        password: doctorForm.password,
        photo_url: photoUrl || '',
        hospital_ids: hospitalIds,
        hospital_id: hospitalIds[0] // For backward compatibility
      };
      
      // Backend will handle if doctor exists (adds hospital) or creates new
      await axios.post(`${API}/doctors`, doctorData);
      
      if (isExistingDoctor) {
        toast.success(`Existing doctor assigned to ${hospitalIds.length} hospital(s)`);
      } else {
        // Show success message with doctor ID and password
        toast.success(
          `Doctor created successfully!\nDoctor ID: ${doctorId}\nPassword: ${doctorForm.password}\nAssigned to ${hospitalIds.length} hospital(s)`,
          { duration: 10000 }
        );
      }
      
      setShowAddDoctorDialog(false);
      setDoctorForm({ name: '', specialization: '', super_specialization: '', registered_number: '', contact: '', password: '', photo_url: '', country_code: '+91' });
      setSelectedPhoto(null);
      setSelectedHospitalForAdd(null);
      setSelectedHospitalsForDoctor([]);
      setSearchedDoctor(null);
      setIsExistingDoctor(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add doctor');
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    
    // Validation: phone and password are mandatory, email is optional
    if (!staffForm.name || !staffForm.phone || !staffForm.password) {
      toast.error('Name, Phone, and Password are required');
      return;
    }

    try {
      // Generate staff ID using hospital code
      const staffId = generateStaffId(selectedHospitalForAdd.code);
      
      const staffData = {
        id: staffId,
        name: staffForm.name,
        phone: staffForm.phone,
        email: staffForm.email || '',
        password: staffForm.password,
        hospital_id: selectedHospitalForAdd.id
      };
      
      await axios.post(`${API}/front-desk-staff`, staffData);
      toast.success(`Staff created with ID: ${staffId}`);
      setShowAddStaffDialog(false);
      setStaffForm({ name: '', phone: '', email: '', password: '', country_code: '+91' });
      setSelectedHospitalForAdd(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    }
  };

  const generateMAId = (hospitalCode) => {
    // Extract last 3 digits from hospital code (e.g., TSHYD304 -> 304)
    const last3Digits = hospitalCode.match(/\d{3}$/)?.[0] || '000';
    const randomNum = Math.floor(100 + Math.random() * 900);
    return `MA${last3Digits}${randomNum}`;
  };

  const handleAddMA = async (e) => {
    e.preventDefault();
    
    // Validation: phone and password are mandatory, email is optional
    if (!maForm.name || !maForm.phone || !maForm.password) {
      toast.error('Name, Phone, and Password are required');
      return;
    }

    try {
      // Generate MA ID using hospital code
      const maId = generateMAId(selectedHospitalForAdd.code);
      
      const maData = {
        id: maId,
        name: maForm.name,
        phone: maForm.phone,
        email: maForm.email || '',
        password: maForm.password,
        hospital_id: selectedHospitalForAdd.id,
        doctor_ids: maForm.doctor_ids || []  // Fixed: use doctor_ids not assigned_doctor_ids
      };
      
      await axios.post(`${API}/medical-assistants`, maData);
      toast.success(`Medical Assistant created with ID: ${maId}`);
      setShowAddMADialog(false);
      setMAForm({ name: '', phone: '', email: '', password: '', doctor_ids: [], country_code: '+91' });
      setSelectedHospitalForAdd(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add Medical Assistant');
    }
  };

  // Open edit MA dialog
  const handleEditMA = (ma) => {
    setEditingMA(ma);
    setMAForm({
      name: ma.name,
      phone: ma.phone || '',
      email: ma.email || '',
      password: '', // Don't pre-fill password for security
      doctor_ids: ma.doctor_ids || [],
      country_code: '+91'
    });
    // Set the hospital for the MA
    const maHospital = hospitals.find(h => h.id === ma.hospital_id);
    setSelectedHospitalForAdd(maHospital);
    setShowAddMADialog(true);
  };

  // Update existing MA
  const handleUpdateMA = async (e) => {
    e.preventDefault();
    
    if (!maForm.name || !maForm.phone) {
      toast.error('Name and Phone are required');
      return;
    }

    try {
      const updateData = {
        name: maForm.name,
        phone: maForm.phone,
        email: maForm.email || '',
        doctor_ids: maForm.doctor_ids || [],
        hospital_id: editingMA.hospital_id  // Required by backend
      };
      
      // Only include password if it's been changed
      if (maForm.password) {
        updateData.password = maForm.password;
      }
      
      await axios.put(`${API}/medical-assistants/${editingMA.id}`, updateData);
      toast.success('Medical Assistant updated successfully');
      setShowAddMADialog(false);
      setEditingMA(null);
      setMAForm({ name: '', phone: '', email: '', password: '', doctor_ids: [], country_code: '+91' });
      setSelectedHospitalForAdd(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Medical Assistant');
    }
  };

  // Delete MA
  const handleDeleteMA = async (maId) => {
    if (!window.confirm('Are you sure you want to delete this Medical Assistant? It will be moved to deleted records and can be restored later.')) {
      return;
    }
    
    const admin = JSON.parse(localStorage.getItem('centralAdmin') || '{}');
    
    try {
      await axios.delete(`${API}/medical-assistants/${maId}`, {
        params: {
          deleted_by_id: admin.username || 'admin',
          deleted_by_name: admin.name || 'Central Admin',
          deleted_by_role: 'central_admin'
        }
      });
      toast.success('Medical Assistant deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete Medical Assistant');
    }
  };

  const handleEditDoctor = (doctor) => {
    setEditingDoctor(doctor);
    setDoctorForm({
      name: doctor.name,
      specialization: doctor.specialization || '',
      super_specialization: doctor.super_specialization || '',
      registered_number: doctor.id.replace('DOC', ''), // Extract registered number from ID
      contact: doctor.contact || '',
      photo_url: doctor.photo_url || '',
      country_code: '+91'
    });
    
    // Load existing hospitals for this doctor
    const doctorHospitalIds = doctor.hospital_ids || (doctor.hospital_id ? [doctor.hospital_id] : []);
    const doctorHospitals = hospitals.filter(h => doctorHospitalIds.includes(h.id));
    setSelectedHospitalsForDoctor(doctorHospitals);
    
    setShowEditDoctorDialog(true);
  };

  const handleUpdateDoctor = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!doctorForm.name || !doctorForm.specialization || !doctorForm.contact) {
      toast.error('Name, Specialization, and Contact are required');
      return;
    }

    try {
      // Upload photo if selected
      let photoUrl = doctorForm.photo_url;
      if (selectedPhoto) {
        photoUrl = await handlePhotoUpload();
      }

      // Get hospital IDs from selected hospitals
      const hospitalIds = selectedHospitalsForDoctor.length > 0 
        ? selectedHospitalsForDoctor.map(h => h.id)
        : (editingDoctor.hospital_ids || [editingDoctor.hospital_id]);

      const doctorData = {
        name: doctorForm.name,
        specialization: doctorForm.specialization,
        super_specialization: doctorForm.super_specialization || null,
        contact: doctorForm.contact,
        password: editingDoctor.password, // CRITICAL FIX: Preserve password
        photo_url: photoUrl || '',
        hospital_ids: hospitalIds,
        hospital_id: hospitalIds[0] // First hospital as primary
      };

      await axios.put(`${API}/doctors/${editingDoctor.id}`, doctorData);
      toast.success(`Doctor updated successfully. Now works at ${hospitalIds.length} hospital(s)`);
      setShowEditDoctorDialog(false);
      setEditingDoctor(null);
      setDoctorForm({ name: '', specialization: '', super_specialization: '', registered_number: '', contact: '', password: '', photo_url: '', country_code: '+91' });
      setSelectedPhoto(null);
      setSelectedHospitalsForDoctor([]);
      setHospitalSearchQuery('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update doctor');
    }
  };

  // Old handleDeleteDoctor removed - now using two-step dialog approach

  const handleEditStaff = (staff) => {
    setEditingStaff(staff);
    setStaffForm({
      name: staff.name,
      phone: staff.phone || '',
      email: staff.email || ''
    });
    setShowEditStaffDialog(true);
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    
    // Validation: phone is mandatory, email is optional
    if (!staffForm.name || !staffForm.phone) {
      toast.error('Name and Phone are required');
      return;
    }

    try {
      const staffData = {
        name: staffForm.name,
        phone: staffForm.phone,
        email: staffForm.email || '',
        password: editingStaff.password, // CRITICAL FIX: Preserve password
        hospital_id: editingStaff.hospital_id
      };

      await axios.put(`${API}/front-desk-staff/${editingStaff.id}`, staffData);
      toast.success('Staff updated successfully');
      setShowEditStaffDialog(false);
      setEditingStaff(null);
      setStaffForm({ name: '', phone: '', email: '', country_code: '+91' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member? It will be moved to deleted records and can be restored later.')) return;
    
    const admin = JSON.parse(localStorage.getItem('centralAdmin') || '{}');
    
    try {
      await axios.delete(`${API}/front-desk-staff/${staffId}`, {
        params: {
          deleted_by_id: admin.username || 'admin',
          deleted_by_name: admin.name || 'Central Admin',
          deleted_by_role: 'central_admin'
        }
      });
      toast.success('Staff deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff');
    }
  };

  // Baby Edit/Delete Handlers
  const handleEditBabyClick = (baby) => {
    setEditingBaby(baby);
    setBabyForm({
      name: baby.name,
      date_of_birth: baby.date_of_birth
    });
    setShowEditBabyDialog(true);
  };

  const handleUpdateBaby = async (e) => {
    e.preventDefault();
    
    if (!babyForm.name || !babyForm.date_of_birth) {
      toast.error('Name and Date of Birth are required');
      return;
    }

    try {
      await axios.put(`${API}/babies/${editingBaby.id}`, {
        name: babyForm.name,
        date_of_birth: babyForm.date_of_birth
      });
      toast.success('Information updated successfully');
      setShowEditBabyDialog(false);
      setEditingBaby(null);
      setBabyForm({ name: '', date_of_birth: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update baby');
    }
  };

  const handleDeleteBabyClick = (baby) => {
    setDeletingBaby(baby);
    setShowDeleteBabyDialog(true);
  };

  const handleDeleteBabyFirstStep = () => {
    setShowDeleteBabyDialog(false);
    setShowDeleteConfirmDialog(true);
    setDeleteConfirmText('');
  };

  const handleDeleteBabyConfirm = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      // Central Admin uses special endpoint that can delete babies with medical records
      await axios.delete(`${API}/central-admin/babies/${deletingBaby.id}`);
      toast.success('Baby and all associated records deleted successfully');
      setShowDeleteConfirmDialog(false);
      setDeletingBaby(null);
      setDeleteConfirmText('');
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete';
      toast.error(errorMessage);
      setShowDeleteConfirmDialog(false);
      setDeletingBaby(null);
      setDeleteConfirmText('');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteBabyDialog(false);
    setShowDeleteConfirmDialog(false);
    setDeletingBaby(null);
    setDeleteConfirmText('');
  };


  // Doctor deletion handlers (similar to baby deletion)
  const handleDeleteDoctorClick = (doctor) => {
    setDeletingDoctor(doctor);
    setShowDeleteDoctorDialog(true);
  };

  const handleDeleteDoctorFirstStep = () => {
    setShowDeleteDoctorDialog(false);
    setShowDeleteDoctorConfirmDialog(true);
    setDeleteDoctorConfirmText('');
  };

  const handleDeleteDoctorConfirm = async () => {
    if (deleteDoctorConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    const admin = JSON.parse(localStorage.getItem('centralAdmin') || '{}');

    try {
      await axios.delete(`${API}/doctors/${deletingDoctor.id}`, {
        params: { 
          force_delete: true,
          deleted_by_id: admin.username || 'admin',
          deleted_by_name: admin.name || 'Central Admin',
          deleted_by_role: 'central_admin'
        }
      });
      toast.success('Doctor deleted successfully');
      setShowDeleteDoctorConfirmDialog(false);
      setDeletingDoctor(null);
      setDeleteDoctorConfirmText('');
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete doctor';
      toast.error(errorMessage);
      setShowDeleteDoctorConfirmDialog(false);
      setDeletingDoctor(null);
      setDeleteDoctorConfirmText('');
    }
  };

  const handleCancelDeleteDoctor = () => {
    setShowDeleteDoctorDialog(false);
    setShowDeleteDoctorConfirmDialog(false);
    setDeletingDoctor(null);
    setDeleteDoctorConfirmText('');
  };


  // Bulk delete handlers
  const handleSelectBaby = (babyId) => {
    setSelectedBabies(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(babyId)) {
        newSelection.delete(babyId);
      } else {
        newSelection.add(babyId);
      }
      return newSelection;
    });
  };

  const handleSelectAllBabies = () => {
    if (selectedBabies.size === filteredBabies.length) {
      setSelectedBabies(new Set());
    } else {
      setSelectedBabies(new Set(filteredBabies.map(baby => baby.id)));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedBabies.size === 0) return;
    setShowBulkDeleteDialog(true);
  };

  const handleBulkDeleteFirstStep = () => {
    setShowBulkDeleteDialog(false);
    setShowBulkDeleteConfirmDialog(true);
    setBulkDeleteConfirmText('');
  };

  const handleBulkDeleteConfirm = async () => {
    if (bulkDeleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      const selectedBabyIds = Array.from(selectedBabies);
      const selectedBabyNames = babies.filter(b => selectedBabies.has(b.id)).map(b => b.name);
      
      // Delete all selected babies using Central Admin endpoint
      const deletePromises = selectedBabyIds.map(babyId => 
        axios.delete(`${API}/central-admin/babies/${babyId}`)
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`Successfully deleted ${selectedBabies.size} babies`);
      setShowBulkDeleteConfirmDialog(false);
      setSelectedBabies(new Set());
      setBulkDeleteConfirmText('');
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete babies';
      toast.error(errorMessage);
      setShowBulkDeleteConfirmDialog(false);
      setSelectedBabies(new Set());
      setBulkDeleteConfirmText('');
    }
  };

  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
    setShowBulkDeleteConfirmDialog(false);
    setSelectedBabies(new Set());
    setBulkDeleteConfirmText('');
  };


  // Medication bulk delete handlers (similar to baby delete)
  const handleBulkDeleteMedicationsClick = () => {
    if (selectedMedications.size === 0) {
      toast.error('Please select medications to delete');
      return;
    }
    setShowBulkDeleteMedicationDialog(true);
  };

  const handleBulkDeleteMedicationsFirstStep = () => {
    setShowBulkDeleteMedicationDialog(false);
    setShowBulkDeleteMedicationConfirmDialog(true);
    setBulkDeleteMedicationConfirmText('');
  };

  const handleBulkDeleteMedicationsConfirm = async () => {
    if (bulkDeleteMedicationConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      const selectedMedicationIds = Array.from(selectedMedications);
      
      // Delete all selected medications
      const deletePromises = selectedMedicationIds.map(medId => 
        axios.delete(`${API}/common-medications/${medId}`)
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`Successfully deleted ${selectedMedications.size} medications`);
      setShowBulkDeleteMedicationConfirmDialog(false);
      setSelectedMedications(new Set());
      setBulkDeleteMedicationConfirmText('');
      await loadCommonMedications(); // Reload the list
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete medications';
      toast.error(errorMessage);
      setShowBulkDeleteMedicationConfirmDialog(false);
      setSelectedMedications(new Set());
      setBulkDeleteMedicationConfirmText('');
    }
  };

  const handleCancelBulkDeleteMedications = () => {
    setShowBulkDeleteMedicationDialog(false);
    setShowBulkDeleteMedicationConfirmDialog(false);
    setSelectedMedications(new Set());
    setBulkDeleteMedicationConfirmText('');
  };

  // Toggle medication selection
  const toggleMedicationSelection = (medId) => {
    const newSelection = new Set(selectedMedications);
    if (newSelection.has(medId)) {
      newSelection.delete(medId);
    } else {
      newSelection.add(medId);
    }
    setSelectedMedications(newSelection);
  };

  // Select/Deselect all filtered medications
  const toggleSelectAllMedications = () => {
    if (selectedMedications.size === filteredMedications.length && filteredMedications.length > 0) {
      setSelectedMedications(new Set());
    } else {
      setSelectedMedications(new Set(filteredMedications.map(med => med.id)));
    }
  };


  const getHospitalName = (hospitalId) => {
    const hospital = hospitals.find(h => h.id === hospitalId);
    return hospital ? hospital.name : 'Unknown';
  };

  const getDoctorHospitals = (doctorId) => {
    // Group doctors by ID and collect all their hospital names
    const doctorRecords = doctors.filter(d => d.id === doctorId);
    const hospitalNames = [...new Set(doctorRecords.map(d => getHospitalName(d.hospital_id)))];
    return hospitalNames;
  };

  const getAppointmentsByHospitalAndMonth = (selectedYear = new Date().getFullYear()) => {
    const grouped = {};
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    
    // Only process appointments from existing hospitals
    const validAppointments = appointments.filter(apt => validHospitalIds.has(apt.hospital_id));
    
    // Initialize all months for selected year for each hospital
    validAppointments.forEach(apt => {
      const hospitalName = getHospitalName(apt.hospital_id);
      if (!grouped[hospitalName]) {
        grouped[hospitalName] = {};
        monthNames.forEach(month => {
          grouped[hospitalName][month] = {
            total: 0,
            completed: 0,
            pending: 0,
            cancelled: 0
          };
        });
      }
    });
    
    // Fill in actual data
    validAppointments.forEach(apt => {
      const hospitalName = getHospitalName(apt.hospital_id);
      let date;
      
      // Handle both date formats: DD/MM/YYYY and YYYY-MM-DD
      if (apt.appointment_date.includes('/')) {
        // DD/MM/YYYY format
        date = new Date(apt.appointment_date.split('/').reverse().join('-'));
      } else {
        // YYYY-MM-DD format
        date = new Date(apt.appointment_date);
      }
      
      const year = date.getFullYear();
      
      // Only count appointments from selected year
      if (year === selectedYear) {
        const month = monthNames[date.getMonth()];
        
        grouped[hospitalName][month].total++;
        if (apt.status === 'completed') grouped[hospitalName][month].completed++;
        if (apt.status === 'scheduled' || apt.status === 'pending') grouped[hospitalName][month].pending++;
        if (apt.status === 'cancelled') grouped[hospitalName][month].cancelled++;
      }
    });
    
    return grouped;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    const date = new Date(year, month - 1, day);
    return formatDateToDDMMYYYY(date);
  };

  // Helper functions for time-based appointment counts
  const getCompletedAppointments = () => {
    // Only count appointments from existing hospitals
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    return appointments.filter(apt => apt.status === 'completed' && validHospitalIds.has(apt.hospital_id)).length;
  };

  const getTodayAppointments = () => {
    const today = new Date();
    const todayStrISO = today.toISOString().split('T')[0]; // YYYY-MM-DD format
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const todayStrDMY = `${day}/${month}/${year}`; // DD/MM/YYYY format
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    
    return appointments.filter(apt => {
      if (!validHospitalIds.has(apt.hospital_id)) return false;
      if (apt.status !== 'completed') return false; // Only completed appointments
      // Handle both date formats
      const aptDate = apt.appointment_date;
      return (aptDate === todayStrISO || aptDate === todayStrDMY);
    }).length;
  };

  const getThisWeekAppointments = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // Saturday
    endOfWeek.setHours(23, 59, 59, 999);
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    
    return appointments.filter(apt => {
      if (!validHospitalIds.has(apt.hospital_id)) return false;
      if (apt.status !== 'completed') return false; // Only completed appointments
      let aptDate;
      // Handle both date formats: DD/MM/YYYY and YYYY-MM-DD
      if (apt.appointment_date.includes('/')) {
        const parts = apt.appointment_date.split('/');
        aptDate = new Date(parts[2], parts[1] - 1, parts[0]); // DD/MM/YYYY
      } else {
        aptDate = new Date(apt.appointment_date);
      }
      return aptDate >= startOfWeek && aptDate <= endOfWeek;
    }).length;
  };

  const getThisMonthAppointments = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    
    return appointments.filter(apt => {
      if (!validHospitalIds.has(apt.hospital_id)) return false;
      if (apt.status !== 'completed') return false; // Only completed appointments
      let aptDate;
      // Handle both date formats: DD/MM/YYYY and YYYY-MM-DD
      if (apt.appointment_date.includes('/')) {
        const parts = apt.appointment_date.split('/');
        aptDate = new Date(parts[2], parts[1] - 1, parts[0]);
      } else {
        aptDate = new Date(apt.appointment_date);
      }
      return aptDate.getMonth() === currentMonth && aptDate.getFullYear() === currentYear;
    }).length;
  };

  const getThisYearAppointments = () => {
    const currentYear = new Date().getFullYear();
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    
    return appointments.filter(apt => {
      if (!validHospitalIds.has(apt.hospital_id)) return false;
      if (apt.status !== 'completed') return false; // Only completed appointments
      let aptDate;
      // Handle both date formats: DD/MM/YYYY and YYYY-MM-DD
      if (apt.appointment_date.includes('/')) {
        const parts = apt.appointment_date.split('/');
        aptDate = new Date(parts[2], parts[1] - 1, parts[0]);
      } else {
        aptDate = new Date(apt.appointment_date);
      }
      return aptDate.getFullYear() === currentYear;
    }).length;
  };

  // Helper functions for all hospitals appointment statistics
  const getAllHospitalsTotal = (year = new Date().getFullYear()) => {
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    return appointments.filter(apt => {
      if (!validHospitalIds.has(apt.hospital_id)) return false;
      let aptDate;
      // Handle both date formats: DD/MM/YYYY and YYYY-MM-DD
      if (apt.appointment_date.includes('/')) {
        aptDate = new Date(apt.appointment_date.split('/').reverse().join('-'));
      } else {
        aptDate = new Date(apt.appointment_date);
      }
      return aptDate.getFullYear() === year;
    }).length;
  };

  const getAllHospitalsCompleted = (year = new Date().getFullYear()) => {
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    return appointments.filter(apt => {
      if (!validHospitalIds.has(apt.hospital_id)) return false;
      let aptDate;
      // Handle both date formats: DD/MM/YYYY and YYYY-MM-DD
      if (apt.appointment_date.includes('/')) {
        aptDate = new Date(apt.appointment_date.split('/').reverse().join('-'));
      } else {
        aptDate = new Date(apt.appointment_date);
      }
      return aptDate.getFullYear() === year && apt.status === 'completed';
    }).length;
  };

  const getAllHospitalsPending = (year = new Date().getFullYear()) => {
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    return appointments.filter(apt => {
      if (!validHospitalIds.has(apt.hospital_id)) return false;
      let aptDate;
      // Handle both date formats: DD/MM/YYYY and YYYY-MM-DD
      if (apt.appointment_date.includes('/')) {
        aptDate = new Date(apt.appointment_date.split('/').reverse().join('-'));
      } else {
        aptDate = new Date(apt.appointment_date);
      }
      return aptDate.getFullYear() === year && (apt.status === 'scheduled' || apt.status === 'pending');
    }).length;
  };

  const getAllHospitalsCancelled = (year = new Date().getFullYear()) => {
    const validHospitalIds = new Set(hospitals.map(h => h.id));
    return appointments.filter(apt => {
      if (!validHospitalIds.has(apt.hospital_id)) return false;
      let aptDate;
      // Handle both date formats: DD/MM/YYYY and YYYY-MM-DD
      if (apt.appointment_date.includes('/')) {
        aptDate = new Date(apt.appointment_date.split('/').reverse().join('-'));
      } else {
        aptDate = new Date(apt.appointment_date);
      }
      return aptDate.getFullYear() === year && apt.status === 'cancelled';
    }).length;
  };

  // Format date to DD/MM/YYYY
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return 'Not specified';
    
    try {
      // Handle DD/MM/YYYY format (already correct)
      if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3 && parts[0].length <= 2) {
          return dateString; // Already in DD/MM/YYYY format
        }
      }
      
      // Handle YYYY-MM-DD format
      if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3 && parts[0].length === 4) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
      }
      
      // Try to parse as date object
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      
      return dateString; // Return as-is if can't parse
    } catch (error) {
      return dateString;
    }
  };

  // Bulk import function removed

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Shield className="w-10 h-10 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>
                  Central Admin Portal
                </h1>
                <p className="text-blue-100 text-sm">System-wide management and oversight</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Database Export Button */}
              <Button
                onClick={handleExportDatabase}
                disabled={exporting}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                title="Export Database Backup"
              >
                {exporting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="ml-2 hidden sm:inline">Export</span>
              </Button>
              {/* Database Import Button */}
              <Button
                onClick={() => setShowImportDialog(true)}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                title="Import Database Backup"
              >
                <Upload className="w-4 h-4" />
                <span className="ml-2 hidden sm:inline">Import</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/20"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Hospitals</p>
                <p className="text-3xl font-bold text-gray-900">{hospitals.length}</p>
              </div>
              <Building className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Doctors</p>
                <p className="text-3xl font-bold text-gray-900">
                  {[...new Set(doctors.map(d => d.id))].length}
                </p>
              </div>
              <Users className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Medical Assistants</p>
                <p className="text-3xl font-bold text-gray-900">{medicalAssistants.length}</p>
              </div>
              <HeartPulse className="w-10 h-10 text-rose-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Front Desk Staff</p>
                <p className="text-3xl font-bold text-gray-900">{frontDeskStaff.length}</p>
              </div>
              <UserCheck className="w-10 h-10 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Babies</p>
                <p className="text-3xl font-bold text-gray-900">{babies.length}</p>
              </div>
              <Baby className="w-10 h-10 text-pink-600" />
            </div>
          </div>
        </div>

        {/* Time-based Appointment Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-sm border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium mb-1">Today</p>
                <p className="text-3xl font-bold text-blue-900">{getTodayAppointments()}</p>
                <p className="text-xs text-blue-600 mt-1">appointments</p>
              </div>
              <Calendar className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-sm border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-medium mb-1">This Week</p>
                <p className="text-3xl font-bold text-green-900">{getThisWeekAppointments()}</p>
                <p className="text-xs text-green-600 mt-1">appointments</p>
              </div>
              <Calendar className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-sm border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700 font-medium mb-1">This Month</p>
                <p className="text-3xl font-bold text-purple-900">{getThisMonthAppointments()}</p>
                <p className="text-xs text-purple-600 mt-1">appointments</p>
              </div>
              <Calendar className="w-10 h-10 text-purple-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 shadow-sm border border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700 font-medium mb-1">This Year</p>
                <p className="text-3xl font-bold text-orange-900">{getThisYearAppointments()}</p>
                <p className="text-xs text-orange-600 mt-1">appointments</p>
              </div>
              <Calendar className="w-10 h-10 text-orange-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700 font-medium mb-1">Total Appointments</p>
                <p className="text-3xl font-bold text-gray-900">{getCompletedAppointments()}</p>
                <p className="text-xs text-gray-600 mt-1">completed</p>
              </div>
              <Calendar className="w-10 h-10 text-gray-600" />
            </div>
          </div>
        </div>

        {/* Sync Indicator */}
        {lastSyncTime && (
          <div className="mb-4 text-center">
            <p className="text-xs text-gray-500">
              <RefreshCw className="w-3 h-3 inline mr-1" />
              Last synced: {lastSyncTime.toLocaleTimeString()} • Smart sync every 30 seconds
            </p>
          </div>
        )}

        {/* Tabs - Dropdown on mobile, horizontal on desktop */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          {/* Mobile Tab Dropdown */}
          <div className="sm:hidden p-3 border-b border-gray-200">
            <select
              value={activeTab}
              onChange={(e) => {
                const value = e.target.value;
                setActiveTab(value);
                if (value === 'configuration') {
                  loadCommonMedications();
                  loadCommonTests();
                }
                if (value === 'settings') {
                  loadBabyAppSettings();
                }
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="overview">📊 Overview</option>
              <option value="hospitals">🏥 Hospitals</option>
              <option value="doctors">👨‍⚕️ Doctors</option>
              <option value="medical-assistants">💗 Medical Assistants</option>
              <option value="staff">👤 Front Desk Staff</option>
              <option value="appointments">📅 Appointments</option>
              <option value="babies">👶 Babies</option>
              <option value="security">🔒 Security & Audit</option>
              <option value="configuration">⚙️ Configuration</option>
              <option value="settings">🔧 Settings</option>
            </select>
          </div>
          
          {/* Desktop Tabs */}
          <div className="hidden sm:flex items-center space-x-1 p-2 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('hospitals')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'hospitals'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Building className="w-4 h-4 inline mr-2" />
              Hospitals
            </button>
            <button
              onClick={() => setActiveTab('doctors')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'doctors'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Doctors
            </button>
            <button
              onClick={() => setActiveTab('medical-assistants')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'medical-assistants'
                  ? 'bg-pink-100 text-pink-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <HeartPulse className="w-4 h-4 inline mr-2" />
              Medical Assistants
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'staff'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <UserCheck className="w-4 h-4 inline mr-2" />
              Front Desk Staff
            </button>
            <button
              onClick={() => setActiveTab('appointments')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'appointments'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Appointments
            </button>
            <button
              onClick={() => setActiveTab('babies')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'babies'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Baby className="w-4 h-4 inline mr-2" />
              Babies
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'security'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              Security
            </button>
            <button
              onClick={() => {
                setActiveTab('configuration');
                loadCommonMedications();
                loadCommonTests();
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'configuration'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Configuration
            </button>
            <button
              onClick={() => {
                setActiveTab('settings');
                loadBabyAppSettings();
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'settings'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-3 sm:p-6">
            {activeTab === 'overview' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg sm:text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    System Overview
                  </h3>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search hospitals..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  {hospitals
                    .filter(hospital => 
                      hospital.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      hospital.code.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((hospital) => {
                      // Calculate counts dynamically - support both old and new doctor format
                      const doctorsCount = doctors.filter(d => 
                        d.hospital_id === hospital.id || 
                        (d.hospital_ids && d.hospital_ids.includes(hospital.id))
                      ).length;
                      const staffCount = frontDeskStaff.filter(s => s.hospital_id === hospital.id).length;
                      const maCount = medicalAssistants.filter(ma => ma.hospital_id === hospital.id).length;
                      const appointmentsCount = appointments.filter(a => a.hospital_id === hospital.id && a.status === 'completed').length;
                      
                      return (
                    <div key={hospital.id} className="p-3 sm:p-4 border border-gray-200 rounded-lg">
                      {/* Hospital Header - Stacked on mobile, horizontal on desktop */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 sm:mb-2">
                        <h4 className="font-semibold text-base sm:text-lg">{hospital.name}</h4>
                        {/* Badges - Wrap on mobile, inline on desktop */}
                        <div className="flex flex-wrap items-center gap-2 sm:space-x-2 sm:gap-0">
                          <span className="text-xs sm:text-sm bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full">
                            ID: {hospital.code}
                          </span>
                          <span className="text-xs sm:text-sm bg-green-100 text-green-700 px-2 sm:px-3 py-1 rounded-full whitespace-nowrap">
                            <span className="sm:hidden">Pass:</span>
                            <span className="hidden sm:inline">Password:</span> {hospital.password}
                          </span>
                          {/* AI Features Toggle for Hospital */}
                          <button
                            onClick={() => handleUpdateHospitalAiFeatures(hospital.id, !(hospital.ai_features_enabled !== false))}
                            className={`flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                              hospital.ai_features_enabled !== false
                                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title={hospital.ai_features_enabled !== false ? 'AI Features Enabled - Click to disable' : 'AI Features Disabled - Click to enable'}
                          >
                            <span className="text-sm sm:text-base">{hospital.ai_features_enabled !== false ? '🤖' : '🚫'}</span>
                            <span className="hidden sm:inline">AI {hospital.ai_features_enabled !== false ? 'On' : 'Off'}</span>
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedHospital(expandedHospital === hospital.id ? null : hospital.id)}
                            className="text-blue-600 text-xs sm:text-sm px-2"
                          >
                            <span className="sm:hidden">{expandedHospital === hospital.id ? 'Less' : 'More'}</span>
                            <span className="hidden sm:inline">{expandedHospital === hospital.id ? 'Minimize' : 'Maximize'}</span>
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm">
                        <div>
                          <p className="text-gray-600">Doctors</p>
                          <p className="font-semibold text-gray-900">{doctorsCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Med. Asst.</p>
                          <p className="font-semibold text-gray-900">{maCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Front Desk</p>
                          <p className="font-semibold text-gray-900">{staffCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Appointments</p>
                          <p className="font-semibold text-gray-900">{appointmentsCount}</p>
                        </div>
                      </div>

                      {/* Expanded View */}
                      {expandedHospital === hospital.id && (
                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                          {/* Doctors List */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold text-gray-900">Doctors ({doctorsCount})</h5>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedHospitalForAdd(hospital);
                                  setShowAddDoctorDialog(true);
                                }}
                                className="bg-green-600 hover:bg-green-700 text-xs"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Doctor
                              </Button>
                            </div>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {doctors.filter(d => 
                                d.hospital_id === hospital.id || 
                                (d.hospital_ids && d.hospital_ids.includes(hospital.id))
                              ).map(doctor => (
                                <div key={doctor.id} className="p-2 bg-gray-50 rounded text-sm">
                                  <p className="font-medium text-gray-900">
                                    {doctor.name} <span className="text-gray-500 text-xs font-normal">(ID: {doctor.id.substring(0, 8)}{doctor.password ? ` | Pwd: ${doctor.password}` : ''})</span>
                                  </p>
                                  <p className="text-gray-600 text-xs">{doctor.specialization || 'General'}</p>
                                  {doctor.contact && (
                                    <p className="text-gray-600 text-xs">{doctor.contact}</p>
                                  )}
                                  {doctor.hospital_ids && doctor.hospital_ids.length > 1 && (
                                    <p className="text-blue-600 text-xs mt-1">
                                      ✓ Works at {doctor.hospital_ids.length} hospitals
                                    </p>
                                  )}
                                </div>
                              ))}
                              {doctorsCount === 0 && (
                                <p className="text-gray-500 text-sm">No doctors assigned</p>
                              )}
                            </div>
                          </div>

                          {/* Front Desk Staff List */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold text-gray-900">Front Desk Staff ({staffCount})</h5>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedHospitalForAdd(hospital);
                                  setShowAddStaffDialog(true);
                                }}
                                className="bg-purple-600 hover:bg-purple-700 text-xs"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Staff
                              </Button>
                            </div>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {frontDeskStaff.filter(s => s.hospital_id === hospital.id).map(staff => (
                                <div key={staff.id} className="p-2 bg-gray-50 rounded text-sm">
                                  <p className="font-medium text-gray-900">
                                    {staff.name} <span className="text-gray-500 text-xs font-normal">(ID: {staff.id}{staff.password ? ` | Pwd: ${staff.password}` : ''})</span>
                                  </p>
                                  {staff.phone && <p className="text-gray-600 text-xs">{staff.phone}</p>}
                                </div>
                              ))}
                              {staffCount === 0 && (
                                <p className="text-gray-500 text-sm">No staff assigned</p>
                              )}
                            </div>
                          </div>

                          {/* Medical Assistants List */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold text-gray-900">Medical Assistants ({maCount})</h5>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedHospitalForAdd(hospital);
                                  setMAForm({ name: '', phone: '', email: '', password: '', doctor_ids: [], country_code: '+91' });
                                  setShowAddMADialog(true);
                                }}
                                className="bg-pink-600 hover:bg-pink-700 text-xs"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add MA
                              </Button>
                            </div>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {medicalAssistants.filter(ma => ma.hospital_id === hospital.id).map(ma => (
                                <div key={ma.id} className="p-2 bg-gray-50 rounded text-sm">
                                  <p className="font-medium text-gray-900">
                                    {ma.name} <span className="text-gray-500 text-xs font-normal">(ID: {ma.id}{ma.password ? ` | Pwd: ${ma.password}` : ''})</span>
                                  </p>
                                  {ma.phone && <p className="text-gray-600 text-xs">{ma.phone}</p>}
                                </div>
                              ))}
                              {maCount === 0 && (
                                <p className="text-gray-500 text-sm">No medical assistants assigned</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'hospitals' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    Hospital Management
                  </h3>
                  <div className="flex items-center space-x-3">
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search hospitals..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <Button
                      onClick={() => setShowAddHospitalDialog(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Hospital
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {hospitals
                    .filter(hospital => 
                      hospital.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      hospital.code.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((hospital) => (
                    <div key={hospital.id} className="p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold text-lg">{hospital.name}</h4>
                            <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                              {hospital.code}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                            {hospital.contact_email && (
                              <div>
                                <p className="text-gray-600">Email</p>
                                <p className="text-gray-900">{hospital.contact_email}</p>
                              </div>
                            )}
                            {hospital.contact_phone && (
                              <div>
                                <p className="text-gray-600">Phone</p>
                                <p className="text-gray-900">{hospital.contact_phone}</p>
                              </div>
                            )}
                          </div>
                          {hospital.address && (
                            <p className="text-sm text-gray-600 mt-2">{hospital.address}</p>
                          )}
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditHospital(hospital)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteHospital(hospital.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {hospitals
                    .filter(hospital => 
                      hospital.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      hospital.code.toLowerCase().includes(searchQuery.toLowerCase())
                    ).length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{searchQuery ? 'No hospitals found matching your search' : 'No hospitals added yet. Click "Add Hospital" to get started.'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'doctors' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    All Doctors
                  </h3>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search doctors..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {doctors
                    .filter(doctor => 
                      doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      doctor.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      (doctor.specialization && doctor.specialization.toLowerCase().includes(searchQuery.toLowerCase()))
                    )
                    .map((doctor) => {
                      // Get all hospitals for this doctor
                      const doctorHospitalIds = doctor.hospital_ids && doctor.hospital_ids.length > 0 
                        ? doctor.hospital_ids 
                        : (doctor.hospital_id ? [doctor.hospital_id] : []);
                      
                      const doctorHospitals = doctorHospitalIds.map(hId => {
                        const hospital = hospitals.find(h => h.id === hId);
                        return {
                          id: hId,
                          name: hospital?.name || 'Unknown Hospital'
                        };
                      });

                      return (
                      <div key={doctor.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-semibold">Dr. {doctor.name}</h4>
                              <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                                {doctor.id}
                              </span>
                              {doctor.password && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
                                  Password: {doctor.password}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {doctor.super_specialization || doctor.specialization || 'General Physician'}
                            </p>
                            {doctor.contact && (
                              <p className="text-sm text-gray-500 mt-1">{doctor.contact}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="flex flex-col gap-2">
                              {doctorHospitals.map((hospital, index) => (
                                <span key={index} className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full text-center">
                                  {hospital.name}
                                </span>
                              ))}
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditDoctor(doctor)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDoctorClick(doctor)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                </div>
              </div>
            )}

            {activeTab === 'medical-assistants' && (
              <div data-testid="central-admin-ma-tab">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    Medical Assistants
                  </h3>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search medical assistants..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {medicalAssistants
                    .filter(ma => 
                      ma.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      (ma.email && ma.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
                      ma.id.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((ma) => (
                    <div key={ma.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow" data-testid={`central-ma-card-${ma.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2 mb-2">
                            <h4 className="font-semibold text-gray-900">{ma.name}</h4>
                            <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full font-medium">
                              {ma.id}
                            </span>
                            {ma.password && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                Password: {ma.password}
                              </span>
                            )}
                          </div>
                          {ma.email && <p className="text-sm text-gray-600">{ma.email}</p>}
                          {ma.phone && <p className="text-sm text-gray-500">{ma.phone}</p>}
                          {ma.doctor_ids && ma.doctor_ids.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 mb-1">Assigned Doctors:</p>
                              <div className="flex flex-wrap gap-1">
                                {ma.doctor_ids.map(docId => {
                                  const doc = doctors.find(d => d.id === docId);
                                  return (
                                    <span key={docId} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                                      {doc ? `Dr. ${doc.name}` : docId}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                            {getHospitalName(ma.hospital_id)}
                          </span>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMA(ma)}
                              className="text-blue-600 hover:text-blue-700"
                              data-testid={`edit-ma-${ma.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMA(ma.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-ma-${ma.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {medicalAssistants.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <HeartPulse className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p>No Medical Assistants found.</p>
                      <p className="text-sm">Medical Assistants can be added from the Hospital Admin dashboard or System Overview.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'staff' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    Front Desk Staff
                  </h3>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search staff..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {frontDeskStaff
                    .filter(staff => 
                      staff.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      staff.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      staff.id.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((staff) => (
                    <div key={staff.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="font-semibold">{staff.name}</h4>
                            <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
                              {staff.id}
                            </span>
                            {staff.password && (
                              <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                                Password: {staff.password}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{staff.email}</p>
                          {staff.phone && (
                            <p className="text-sm text-gray-500 mt-1">{staff.phone}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                            {getHospitalName(staff.hospital_id)}
                          </span>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditStaff(staff)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteStaff(staff.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'appointments' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    Appointments by Hospital ({selectedAppointmentYear})
                  </h3>
                  <div className="flex items-center space-x-4">
                    <select
                      value={selectedAppointmentYear}
                      onChange={(e) => setSelectedAppointmentYear(parseInt(e.target.value))}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {[2023, 2024, 2025, 2026, 2027].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search hospitals..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                {/* Statistics Cards for All Hospitals */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-sm border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-700 font-medium mb-1">All Hospitals</p>
                        <p className="text-3xl font-bold text-blue-900">{getAllHospitalsTotal(selectedAppointmentYear)}</p>
                        <p className="text-xs text-blue-600 mt-1">Total Appointments</p>
                      </div>
                      <Calendar className="w-10 h-10 text-blue-600" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-sm border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-700 font-medium mb-1">All Hospitals</p>
                        <p className="text-3xl font-bold text-green-900">{getAllHospitalsCompleted(selectedAppointmentYear)}</p>
                        <p className="text-xs text-green-600 mt-1">Completed</p>
                      </div>
                      <Calendar className="w-10 h-10 text-green-600" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 shadow-sm border border-yellow-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-yellow-700 font-medium mb-1">All Hospitals</p>
                        <p className="text-3xl font-bold text-yellow-900">{getAllHospitalsPending(selectedAppointmentYear)}</p>
                        <p className="text-xs text-yellow-600 mt-1">Pending</p>
                      </div>
                      <Calendar className="w-10 h-10 text-yellow-600" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 shadow-sm border border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-red-700 font-medium mb-1">All Hospitals</p>
                        <p className="text-3xl font-bold text-red-900">{getAllHospitalsCancelled(selectedAppointmentYear)}</p>
                        <p className="text-xs text-red-600 mt-1">Cancelled</p>
                      </div>
                      <Calendar className="w-10 h-10 text-red-600" />
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {Object.entries(getAppointmentsByHospitalAndMonth(selectedAppointmentYear))
                    .filter(([hospitalName]) => 
                      hospitalName !== 'Unknown' && 
                      hospitalName.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map(([hospitalName, months]) => {
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const yearTotal = Object.values(months).reduce((sum, m) => sum + m.total, 0);
                    const yearCompleted = Object.values(months).reduce((sum, m) => sum + m.completed, 0);
                    const yearPending = Object.values(months).reduce((sum, m) => sum + m.pending, 0);
                    const yearCancelled = Object.values(months).reduce((sum, m) => sum + m.cancelled, 0);
                    
                    return (
                      <div key={hospitalName} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                          <h4 className="font-semibold text-lg">{hospitalName}</h4>
                          <p className="text-sm text-gray-600">
                            Year Total: {yearTotal} | Completed: {yearCompleted} | Pending: {yearPending} | Cancelled: {yearCancelled}
                          </p>
                        </div>
                        
                        <div className="p-4 overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-left py-2 px-2 font-semibold text-gray-700"></th>
                                {monthNames.map(month => (
                                  <th key={month} className="text-center py-2 px-2 font-semibold text-gray-700">{month}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-gray-100">
                                <td className="py-2 px-2 font-medium text-gray-600">Total</td>
                                {monthNames.map(month => (
                                  <td key={month} className="text-center py-2 px-2 text-gray-900 font-medium">
                                    {months[month].total || 0}
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-b border-gray-100">
                                <td className="py-2 px-2 font-medium text-gray-600">Completed</td>
                                {monthNames.map(month => (
                                  <td key={month} className="text-center py-2 px-2 text-green-600 font-medium">
                                    {months[month].completed || 0}
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-b border-gray-100">
                                <td className="py-2 px-2 font-medium text-gray-600">Pending</td>
                                {monthNames.map(month => (
                                  <td key={month} className="text-center py-2 px-2 text-yellow-600 font-medium">
                                    {months[month].pending || 0}
                                  </td>
                                ))}
                              </tr>
                              <tr>
                                <td className="py-2 px-2 font-medium text-gray-600">Cancelled</td>
                                {monthNames.map(month => (
                                  <td key={month} className="text-center py-2 px-2 text-red-600 font-medium">
                                    {months[month].cancelled || 0}
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  
                  {Object.keys(getAppointmentsByHospitalAndMonth(selectedAppointmentYear)).filter((hospitalName) => 
                    hospitalName !== 'Unknown' && 
                    hospitalName.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>{searchQuery ? 'No hospitals found matching your search' : 'No appointments found'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Babies Tab */}
            {activeTab === 'babies' && (
              <div>
                {/* Baby Creation Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Total Babies</p>
                        <p className="text-3xl font-bold text-gray-900">{babyStats.total_babies}</p>
                      </div>
                      <Baby className="w-10 h-10 text-blue-600" />
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Created via Baby App</p>
                        <p className="text-3xl font-bold text-gray-900">{babyStats.created_via_baby_app}</p>
                        <p className="text-xs text-gray-500 mt-1">Parent Registration</p>
                      </div>
                      <User className="w-10 h-10 text-green-600" />
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Created via Appointments</p>
                        <p className="text-3xl font-bold text-gray-900">{babyStats.created_via_appointment}</p>
                        <p className="text-xs text-gray-500 mt-1">Front Desk Registration</p>
                      </div>
                      <Calendar className="w-10 h-10 text-purple-600" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    All Babies
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search babies..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                {(() => {
                  return (
                    <>
                      {filteredBabies.length > 0 && (
                        <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedBabies.size === filteredBabies.length && filteredBabies.length > 0}
                                onChange={handleSelectAllBabies}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Select All ({filteredBabies.length})
                              </span>
                            </label>
                            {selectedBabies.size > 0 && (
                              <span className="text-sm text-blue-600 font-medium">
                                {selectedBabies.size} selected
                              </span>
                            )}
                          </div>
                          
                          {selectedBabies.size > 0 && (
                            <Button
                              onClick={handleBulkDeleteClick}
                              variant="destructive"
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete Selected ({selectedBabies.size})
                            </Button>
                          )}
                        </div>
                      )}

                      <div className="space-y-3">
                        {filteredBabies.map((baby) => (
                        <div key={baby.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <input
                                type="checkbox"
                                checked={selectedBabies.has(baby.id)}
                                onChange={() => handleSelectBaby(baby.id)}
                                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h4 className="font-semibold text-lg text-gray-900">
                                    {baby.name} <span className="text-gray-500 text-xs font-normal">(ID: {baby.short_id || baby.id.substring(0, 8).toUpperCase()})</span>
                                  </h4>
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    ['male', 'boy', 'm'].includes(baby.gender?.toLowerCase()) ? 'bg-blue-100 text-blue-700' :
                                    ['female', 'girl', 'f'].includes(baby.gender?.toLowerCase()) ? 'bg-pink-100 text-pink-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {normalizeGender(baby.gender)}
                                  </span>
                                </div>
                                
                                <div className="text-sm">
                                  <p className="text-gray-600">
                                    Date of Birth: <span className="text-gray-900 font-medium">{formatDateToDDMMYYYY(baby.date_of_birth)}</span>
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Edit and Delete Buttons */}
                            <div className="flex items-center space-x-2 ml-4">
                              <Button
                                onClick={() => handleEditBabyClick(baby)}
                                variant="outline"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                onClick={() => handleDeleteBabyClick(baby)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                        ))}
                        
                        {filteredBabies.length === 0 && (
                          <div className="text-center py-12 text-gray-500">
                            <Baby className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p>{searchQuery ? 'No babies found matching your search' : 'No babies registered yet'}</p>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Security & Audit Tab */}
            {activeTab === 'security' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    <Shield className="w-6 h-6 inline mr-2 text-blue-600" />
                    Security & Audit Logs
                  </h3>
                </div>
                <SecurityAuditTab hospitals={hospitals} isHospitalAdmin={false} />
              </div>
            )}

            {/* Configuration Tab */}
            {activeTab === 'configuration' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    System Configuration
                  </h3>
                </div>

                {/* Common Tests Section */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">Common Tests</h4>
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-gray-500">
                        {commonTests.length} tests
                      </div>
                      <Button
                        onClick={() => setShowAddTestDialog(true)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Test
                      </Button>
                    </div>
                  </div>

                  {/* Search Bar for Tests */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search tests..."
                      value={testSearchQuery}
                      onChange={(e) => setTestSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Tests List - Grouped by Test Type and Category */}
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {(() => {
                      // Filter tests based on search query
                      const filteredTests = commonTests.filter(test => 
                        test.test_name.toLowerCase().includes(testSearchQuery.toLowerCase()) ||
                        (test.test_type && test.test_type.toLowerCase().includes(testSearchQuery.toLowerCase())) ||
                        (test.category && test.category.toLowerCase().includes(testSearchQuery.toLowerCase()))
                      );

                      if (filteredTests.length === 0) {
                        return (
                          <div className="text-center py-12 text-gray-500">
                            <Settings className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p>{testSearchQuery ? 'No tests found matching your search' : 'No common tests configured yet'}</p>
                          </div>
                        );
                      }

                      // Group tests by test type
                      const groupedByType = filteredTests.reduce((acc, test) => {
                        const type = test.test_type || 'Other';
                        if (!acc[type]) acc[type] = [];
                        acc[type].push(test);
                        return acc;
                      }, {});

                      // Auto-expand all groups when searching
                      const isExpanded = (key) => testSearchQuery ? true : expandedTestTypes[key];
                      const isCategoryExpanded = (key) => testSearchQuery ? true : expandedCategories[key];

                      return Object.entries(groupedByType).map(([testType, tests]) => (
                        <div key={testType} className="border border-gray-200 rounded-lg">
                          {/* Test Type Header */}
                          <button
                            onClick={() => setExpandedTestTypes(prev => ({ ...prev, [testType]: !prev[testType] }))}
                            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
                          >
                            <div className="flex items-center space-x-2">
                              {isExpanded(testType) ? (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              )}
                              <span className="font-semibold text-gray-900">{testType}</span>
                              <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded">{tests.length} tests</span>
                            </div>
                          </button>

                          {/* Test Type Content */}
                          {isExpanded(testType) && (
                            <div className="p-2">
                              {testType === 'Blood Test' ? (
                                // For Blood Tests, group by category
                                (() => {
                                  const groupedByCategory = tests.reduce((acc, test) => {
                                    const category = test.category || 'Uncategorized';
                                    if (!acc[category]) acc[category] = [];
                                    acc[category].push(test);
                                    return acc;
                                  }, {});

                                  return Object.entries(groupedByCategory).map(([category, categoryTests]) => (
                                    <div key={category} className="mb-2">
                                      {/* Category Header */}
                                      <button
                                        onClick={() => setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                                        className="w-full flex items-center justify-between p-2 bg-blue-50 hover:bg-blue-100 transition-colors rounded"
                                      >
                                        <div className="flex items-center space-x-2">
                                          {isCategoryExpanded(category) ? (
                                            <ChevronDown className="w-3 h-3 text-blue-600" />
                                          ) : (
                                            <ChevronRight className="w-3 h-3 text-blue-600" />
                                          )}
                                          <span className="text-sm font-medium text-blue-900">{category}</span>
                                          <span className="text-xs text-blue-600 bg-white px-2 py-0.5 rounded">{categoryTests.length}</span>
                                        </div>
                                      </button>

                                      {/* Category Tests */}
                                      {isCategoryExpanded(category) && (
                                        <div className="mt-1 space-y-1 ml-4">
                                          {categoryTests.map((test) => (
                                            <div key={test.id} className="flex items-center justify-between p-3 border border-gray-200 rounded hover:shadow-sm transition-shadow bg-white">
                                              <div className="flex-1">
                                                <h5 className="font-medium text-gray-900">{test.test_name}</h5>
                                                {test.description && (
                                                  <p className="text-xs text-gray-600 mt-1">{test.description}</p>
                                                )}
                                              </div>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                  setTestToDelete(test);
                                                  setShowDeleteTestDialog(true);
                                                }}
                                                className="text-red-600 hover:text-red-700"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ));
                                })()
                              ) : (
                                // For non-Blood Tests, show tests directly
                                <div className="space-y-1">
                                  {tests.map((test) => (
                                    <div key={test.id} className="flex items-center justify-between p-3 border border-gray-200 rounded hover:shadow-sm transition-shadow bg-white">
                                      <div className="flex-1">
                                        <h5 className="font-medium text-gray-900">{test.test_name}</h5>
                                        {test.description && (
                                          <p className="text-xs text-gray-600 mt-1">{test.description}</p>
                                        )}
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setTestToDelete(test);
                                          setShowDeleteTestDialog(true);
                                        }}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Common Medications Section */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">Common Medications</h4>
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-gray-500">
                        {loadingMedications ? 'Loading...' : `${commonMedications.length} medications`}
                        {selectedMedications.size > 0 && ` (${selectedMedications.size} selected)`}
                      </div>
                      <Button
                        onClick={() => setShowAddMedicationDialog(true)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Medication
                      </Button>
                      <Button
                        onClick={() => setShowBulkImportDialog(true)}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Bulk Import
                      </Button>
                    </div>
                  </div>

                  {/* Alphabet Filter */}
                  <div className="flex flex-wrap gap-1 mb-4 pb-4 border-b">
                    <button
                      onClick={() => loadCommonMedications('')}
                      className={`px-2 py-1 text-xs rounded ${selectedMedicationLetter === '' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      All
                    </button>
                    {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => (
                      <button
                        key={letter}
                        onClick={() => loadCommonMedications(letter)}
                        className={`px-2 py-1 text-xs rounded ${selectedMedicationLetter === letter ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>

                  {/* Loading indicator */}
                  {loadingMedications && (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-500 border-t-transparent"></div>
                      <span className="ml-3 text-gray-500">Loading medications...</span>
                    </div>
                  )}

                  {/* Bulk Actions */}
                  {!loadingMedications && commonMedications.length > 0 && (
                    <div className="flex items-center justify-between mb-4 pb-4 border-b">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedMedications.size === filteredMedications.length && filteredMedications.length > 0}
                          onChange={toggleSelectAllMedications}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-600">Select All ({filteredMedications.length})</span>
                      </div>
                      {selectedMedications.size > 0 && (
                        <Button
                          onClick={handleBulkDeleteMedicationsClick}
                          size="sm"
                          variant="destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Selected ({selectedMedications.size})
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Search Bar for Medications */}
                  {!loadingMedications && (
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search medications..."
                        value={medicationSearchQuery}
                        onChange={(e) => setMedicationSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  )}

                  {/* Medications List */}
                  {!loadingMedications && (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {filteredMedications.map((medication) => (
                        <div key={medication.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                          <input
                            type="checkbox"
                            checked={selectedMedications.has(medication.id)}
                            onChange={() => toggleMedicationSelection(medication.id)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              {medication.medication_type && (
                                <MedicationIcon type={medication.medication_type.toLowerCase()} size={20} />
                              )}
                              <h5 className="font-medium text-gray-900">{medication.medication_name}</h5>
                              {medication.category && (
                                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{medication.category}</span>
                              )}
                            </div>
                            {medication.generic_name && (
                              <p className="text-sm text-gray-600 mt-1">Generic: {medication.generic_name}</p>
                            )}
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                              {medication.default_dosage && (
                                <span>Dosage: {medication.default_dosage}</span>
                              )}
                              {medication.default_timing && (
                                <span>Timing: {medication.default_timing}</span>
                              )}
                              {medication.default_duration && (
                                <span>Duration: {medication.default_duration}</span>
                              )}
                            </div>
                            {medication.notes && (
                              <p className="text-xs text-gray-500 mt-1">{medication.notes}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setMedicationToDelete(medication);
                              setShowDeleteMedicationDialog(true);
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    
                      {filteredMedications.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                          <Settings className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p>{medicationSearchQuery ? 'No medications found matching your search' : 'No common medications configured yet'}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                    Baby App Settings
                  </h3>
                </div>

                {/* Baby App Access Control */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Access Control</h4>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure access settings for the Baby/Patient app
                  </p>

                  <div className="space-y-6">
                    {/* Baby Login Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 mb-1">Enable Baby Login</h5>
                        <p className="text-sm text-gray-600">
                          Allow users to log into the Baby app
                        </p>
                      </div>
                      <button
                        onClick={() => handleUpdateBabyAppSettings('login_enabled', !babyAppSettings.login_enabled)}
                        disabled={loadingSettings}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          babyAppSettings.login_enabled ? 'bg-green-600' : 'bg-gray-300'
                        } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            babyAppSettings.login_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Profile Creation Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 mb-1">Enable Profile Creation</h5>
                        <p className="text-sm text-gray-600">
                          Allow users to create new baby profiles in the Baby app
                        </p>
                      </div>
                      <button
                        onClick={() => handleUpdateBabyAppSettings('profile_creation_enabled', !babyAppSettings.profile_creation_enabled)}
                        disabled={loadingSettings}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          babyAppSettings.profile_creation_enabled ? 'bg-green-600' : 'bg-gray-300'
                        } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            babyAppSettings.profile_creation_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Join & Sync Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 mb-1">Enable Join & Sync</h5>
                        <p className="text-sm text-gray-600">
                          Allow users to access Join & Sync feature on the login page
                        </p>
                      </div>
                      <button
                        onClick={() => handleUpdateBabyAppSettings('join_sync_enabled', !babyAppSettings.join_sync_enabled)}
                        disabled={loadingSettings}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          babyAppSettings.join_sync_enabled ? 'bg-green-600' : 'bg-gray-300'
                        } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            babyAppSettings.join_sync_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Status Indicator */}
                  <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Current Status:</strong>
                      {' '}
                      {babyAppSettings.login_enabled && babyAppSettings.profile_creation_enabled && babyAppSettings.join_sync_enabled && 'Baby app is fully accessible with all features enabled'}
                      {babyAppSettings.login_enabled && babyAppSettings.profile_creation_enabled && !babyAppSettings.join_sync_enabled && 'Baby app accessible, Join & Sync disabled'}
                      {babyAppSettings.login_enabled && !babyAppSettings.profile_creation_enabled && babyAppSettings.join_sync_enabled && 'Login and Join & Sync enabled, profile creation disabled'}
                      {babyAppSettings.login_enabled && !babyAppSettings.profile_creation_enabled && !babyAppSettings.join_sync_enabled && 'Only login enabled'}
                      {!babyAppSettings.login_enabled && 'Baby app login is disabled'}
                    </p>
                  </div>
                </div>

                {/* AI Features Master Control */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-2xl">🤖</span> AI Features Control
                  </h4>
                  <p className="text-sm text-gray-600 mb-6">
                    Master control for AI-powered features across all hospitals. When disabled here, AI features will be hidden for all hospitals regardless of individual settings.
                  </p>

                  <div className="space-y-6">
                    {/* Consult Assist Toggle (formerly AI Clinical Assistant) */}
                    <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex-1">
                        <h5 className="font-medium text-purple-900 mb-1">Consult Assist</h5>
                        <p className="text-sm text-purple-700">
                          Voice-to-SOAP notes, entity extraction, and AI-powered clinical documentation
                        </p>
                      </div>
                      <button
                        onClick={() => handleUpdateAiFeatureSettings('ai_clinical_assistant_enabled', !aiFeatureSettings.ai_clinical_assistant_enabled)}
                        disabled={loadingAiSettings}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          aiFeatureSettings.ai_clinical_assistant_enabled ? 'bg-purple-600' : 'bg-gray-300'
                        } ${loadingAiSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            aiFeatureSettings.ai_clinical_assistant_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Differential Diagnosis Toggle */}
                    <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex-1">
                        <h5 className="font-medium text-amber-900 mb-1">Differential Diagnosis</h5>
                        <p className="text-sm text-amber-700">
                          AI-suggested differential diagnoses based on chief complaints and symptoms
                        </p>
                      </div>
                      <button
                        onClick={() => handleUpdateAiFeatureSettings('differential_diagnosis_enabled', !aiFeatureSettings.differential_diagnosis_enabled)}
                        disabled={loadingAiSettings}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          aiFeatureSettings.differential_diagnosis_enabled ? 'bg-amber-600' : 'bg-gray-300'
                        } ${loadingAiSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            aiFeatureSettings.differential_diagnosis_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Voice Rx Toggle (formerly Voice Dictation) */}
                    <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                      <div className="flex-1">
                        <h5 className="font-medium text-indigo-900 mb-1">Voice Rx</h5>
                        <p className="text-sm text-indigo-700">
                          Enable voice dictation button in session form for prescription and clinical notes
                        </p>
                      </div>
                      <button
                        onClick={() => handleUpdateAiFeatureSettings('voice_dictation_enabled', !aiFeatureSettings.voice_dictation_enabled)}
                        disabled={loadingAiSettings}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          aiFeatureSettings.voice_dictation_enabled ? 'bg-indigo-600' : 'bg-gray-300'
                        } ${loadingAiSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            aiFeatureSettings.voice_dictation_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* AI Features Status Indicator */}
                  <div className={`mt-6 p-3 rounded-lg border ${
                    aiFeatureSettings.ai_clinical_assistant_enabled || aiFeatureSettings.differential_diagnosis_enabled || aiFeatureSettings.voice_dictation_enabled
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <p className={`text-sm ${
                      aiFeatureSettings.ai_clinical_assistant_enabled || aiFeatureSettings.differential_diagnosis_enabled || aiFeatureSettings.voice_dictation_enabled
                        ? 'text-green-800'
                        : 'text-red-800'
                    }`}>
                      <strong>AI Status:</strong>
                      {' '}
                      {(() => {
                        const features = [];
                        if (aiFeatureSettings.ai_clinical_assistant_enabled) features.push('Consult Assist');
                        if (aiFeatureSettings.differential_diagnosis_enabled) features.push('Differential Diagnosis');
                        if (aiFeatureSettings.voice_dictation_enabled) features.push('Voice Rx');
                        
                        if (features.length === 3) return 'All AI features enabled globally';
                        if (features.length === 0) return 'All AI features disabled globally';
                        return `${features.join(', ')} enabled`;
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Hospital Dialog */}
      <Dialog open={showAddHospitalDialog} onOpenChange={setShowAddHospitalDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Hospital</DialogTitle>
            <DialogDescription>
              Create a new hospital with contact details and branding
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddHospital} className="space-y-4">
            <div>
              <Label htmlFor="name">Hospital Name *</Label>
              <Input
                id="name"
                value={hospitalForm.name}
                onChange={(e) => setHospitalForm({...hospitalForm, name: e.target.value})}
                placeholder="e.g., Apollo Hospital"
                required
              />
            </div>
            <div>
              <Label htmlFor="address_line1">Address Line 1 *</Label>
              <Input
                id="address_line1"
                value={hospitalForm.address_line1}
                onChange={(e) => setHospitalForm({...hospitalForm, address_line1: e.target.value})}
                placeholder="Street address, P.O. box"
                required
              />
            </div>
            <div>
              <Label htmlFor="address_line2">Address Line 2</Label>
              <Input
                id="address_line2"
                value={hospitalForm.address_line2}
                onChange={(e) => setHospitalForm({...hospitalForm, address_line2: e.target.value})}
                placeholder="Apartment, suite, unit, building, floor, etc."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={hospitalForm.city}
                  onChange={(e) => setHospitalForm({...hospitalForm, city: e.target.value})}
                  placeholder="e.g., Hyderabad"
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">State *</Label>
                <select
                  id="state"
                  value={hospitalForm.state}
                  onChange={(e) => setHospitalForm({...hospitalForm, state: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select State</option>
                  <option value="AP">Andhra Pradesh</option>
                  <option value="AR">Arunachal Pradesh</option>
                  <option value="AS">Assam</option>
                  <option value="BR">Bihar</option>
                  <option value="CG">Chhattisgarh</option>
                  <option value="GA">Goa</option>
                  <option value="GJ">Gujarat</option>
                  <option value="HR">Haryana</option>
                  <option value="HP">Himachal Pradesh</option>
                  <option value="JH">Jharkhand</option>
                  <option value="KA">Karnataka</option>
                  <option value="KL">Kerala</option>
                  <option value="MP">Madhya Pradesh</option>
                  <option value="MH">Maharashtra</option>
                  <option value="MN">Manipur</option>
                  <option value="ML">Meghalaya</option>
                  <option value="MZ">Mizoram</option>
                  <option value="NL">Nagaland</option>
                  <option value="OD">Odisha</option>
                  <option value="PB">Punjab</option>
                  <option value="RJ">Rajasthan</option>
                  <option value="SK">Sikkim</option>
                  <option value="TN">Tamil Nadu</option>
                  <option value="TS">Telangana</option>
                  <option value="TR">Tripura</option>
                  <option value="UP">Uttar Pradesh</option>
                  <option value="UK">Uttarakhand</option>
                  <option value="WB">West Bengal</option>
                  <option value="DL">Delhi</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="pincode">Pincode *</Label>
              <Input
                id="pincode"
                type="tel"
                value={hospitalForm.pincode}
                onChange={(e) => {
                  const input = e.target.value;
                  // Only allow digits and limit to 6 characters
                  const cleaned = input.replace(/\D/g, '').substring(0, 6);
                  setHospitalForm({...hospitalForm, pincode: cleaned});
                }}
                placeholder="e.g., 500003"
                maxLength={6}
                pattern="[0-9]{6}"
                title="Please enter a valid 6-digit pincode"
                required
              />
              {hospitalForm.pincode && hospitalForm.pincode.length < 6 && (
                <span className="text-xs text-red-500 mt-1 block">
                  {6 - hospitalForm.pincode.length} more digit(s) required
                </span>
              )}
            </div>
            <div>
              <Label htmlFor="contact_phone">Contact Phone *</Label>
              <PhoneInput
                value={hospitalForm.contact_phone}
                onChange={(value) => setHospitalForm({...hospitalForm, contact_phone: value})}
                countryCode={hospitalForm.country_code}
                onCountryCodeChange={(code) => setHospitalForm({...hospitalForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            <div>
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={hospitalForm.contact_email}
                onChange={(e) => setHospitalForm({...hospitalForm, contact_email: e.target.value})}
                placeholder="e.g., contact@hospital.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Administrator Password *</Label>
              <Input
                id="password"
                type="password"
                value={hospitalForm.password}
                onChange={(e) => setHospitalForm({...hospitalForm, password: e.target.value})}
                placeholder="Enter administrator password"
                required
              />
              <p className="text-xs text-gray-500 mt-1">This password will be used by hospital administrators to login</p>
            </div>
            {hospitalForm.state && hospitalForm.city && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">Hospital ID will be:</p>
                <p className="font-mono mt-1">
                  {hospitalForm.state}{hospitalForm.city.substring(0,3).toUpperCase()}XXX
                </p>
                <p className="text-xs mt-1 text-blue-600">Auto-generated based on State and City</p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={!hospitalForm.name || !hospitalForm.address_line1 || !hospitalForm.city || !hospitalForm.state || !hospitalForm.pincode || !hospitalForm.password}
              >
                Add Hospital
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setShowAddHospitalDialog(false);
                setHospitalForm({
                  name: '',
                  address_line1: '',
                  address_line2: '',
                  city: '',
                  state: '',
                  pincode: '',
                  contact_email: '',
                  contact_phone: '',
                  password: '',
                  primary_color: '#3B82F6',
                  secondary_color: '#8B5CF6'
                });
              }} className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Hospital Dialog */}
      <Dialog open={showEditHospitalDialog} onOpenChange={setShowEditHospitalDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Hospital</DialogTitle>
            <DialogDescription>
              Update hospital information and settings
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateHospital} className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Hospital Name *</Label>
              <Input
                id="edit_name"
                value={hospitalForm.name}
                onChange={(e) => setHospitalForm({...hospitalForm, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_address_line1">Address Line 1 *</Label>
              <Input
                id="edit_address_line1"
                value={hospitalForm.address_line1}
                onChange={(e) => setHospitalForm({...hospitalForm, address_line1: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_address_line2">Address Line 2</Label>
              <Input
                id="edit_address_line2"
                value={hospitalForm.address_line2}
                onChange={(e) => setHospitalForm({...hospitalForm, address_line2: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_city">City *</Label>
                <Input
                  id="edit_city"
                  value={hospitalForm.city}
                  onChange={(e) => setHospitalForm({...hospitalForm, city: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_state">State *</Label>
                <select
                  id="edit_state"
                  value={hospitalForm.state}
                  onChange={(e) => setHospitalForm({...hospitalForm, state: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select State</option>
                  <option value="AP">Andhra Pradesh</option>
                  <option value="AR">Arunachal Pradesh</option>
                  <option value="AS">Assam</option>
                  <option value="BR">Bihar</option>
                  <option value="CG">Chhattisgarh</option>
                  <option value="GA">Goa</option>
                  <option value="GJ">Gujarat</option>
                  <option value="HR">Haryana</option>
                  <option value="HP">Himachal Pradesh</option>
                  <option value="JH">Jharkhand</option>
                  <option value="KA">Karnataka</option>
                  <option value="KL">Kerala</option>
                  <option value="MP">Madhya Pradesh</option>
                  <option value="MH">Maharashtra</option>
                  <option value="MN">Manipur</option>
                  <option value="ML">Meghalaya</option>
                  <option value="MZ">Mizoram</option>
                  <option value="NL">Nagaland</option>
                  <option value="OD">Odisha</option>
                  <option value="PB">Punjab</option>
                  <option value="RJ">Rajasthan</option>
                  <option value="SK">Sikkim</option>
                  <option value="TN">Tamil Nadu</option>
                  <option value="TS">Telangana</option>
                  <option value="TR">Tripura</option>
                  <option value="UP">Uttar Pradesh</option>
                  <option value="UK">Uttarakhand</option>
                  <option value="WB">West Bengal</option>
                  <option value="DL">Delhi</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="edit_pincode">Pincode *</Label>
              <Input
                id="edit_pincode"
                type="tel"
                value={hospitalForm.pincode}
                onChange={(e) => {
                  const input = e.target.value;
                  // Only allow digits and limit to 6 characters
                  const cleaned = input.replace(/\D/g, '').substring(0, 6);
                  setHospitalForm({...hospitalForm, pincode: cleaned});
                }}
                maxLength={6}
                pattern="[0-9]{6}"
                title="Please enter a valid 6-digit pincode"
                required
              />
              {hospitalForm.pincode && hospitalForm.pincode.length < 6 && (
                <span className="text-xs text-red-500 mt-1 block">
                  {6 - hospitalForm.pincode.length} more digit(s) required
                </span>
              )}
            </div>
            <div>
              <Label htmlFor="edit_contact_phone">Contact Phone *</Label>
              <PhoneInput
                value={hospitalForm.contact_phone}
                onChange={(value) => setHospitalForm({...hospitalForm, contact_phone: value})}
                countryCode={hospitalForm.country_code}
                onCountryCodeChange={(code) => setHospitalForm({...hospitalForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_contact_email">Contact Email</Label>
              <Input
                id="edit_contact_email"
                type="email"
                value={hospitalForm.contact_email}
                onChange={(e) => setHospitalForm({...hospitalForm, contact_email: e.target.value})}
              />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
              <p className="font-medium">Hospital ID: {editingHospital?.code}</p>
              <p className="text-xs mt-1 text-gray-500">ID cannot be changed after creation</p>
            </div>
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={!hospitalForm.name || !hospitalForm.address_line1 || !hospitalForm.city || !hospitalForm.state || !hospitalForm.pincode}
              >
                Update Hospital
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditHospitalDialog(false);
                  setEditingHospital(null);
                  setHospitalForm({
                    name: '',
                    address_line1: '',
                    address_line2: '',
                    city: '',
                    state: '',
                    pincode: '',
                    contact_email: '',
                    contact_phone: '',
                    password: '',
                    primary_color: '#3B82F6',
                    secondary_color: '#8B5CF6'
                  });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Doctor Dialog */}
      <Dialog open={showAddDoctorDialog} onOpenChange={(open) => {
        setShowAddDoctorDialog(open);
        if (!open) {
          // Reset all states when dialog closes
          setDoctorForm({ name: '', specialization: '', super_specialization: '', registered_number: '', contact: '', password: '', photo_url: '', country_code: '+91' });
          setSearchedDoctor(null);
          setIsExistingDoctor(false);
          setSelectedPhoto(null);
        }
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Doctor to {selectedHospitalForAdd?.name}</DialogTitle>
            <DialogDescription>
              Search for existing doctor or add a new doctor to this hospital
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddDoctor} className="space-y-4">
            {/* Registration Number - Moved to Top with Search */}
            <div>
              <Label htmlFor="registered_number">Registered Number *</Label>
              <div className="flex gap-2">
                <Input
                  id="registered_number"
                  value={doctorForm.registered_number}
                  onChange={(e) => {
                    setDoctorForm({...doctorForm, registered_number: e.target.value});
                    // Reset search state when user modifies registration number
                    if (searchedDoctor) {
                      setSearchedDoctor(null);
                      setIsExistingDoctor(false);
                    }
                  }}
                  placeholder="e.g., 12345"
                  required
                  disabled={isExistingDoctor}
                  className={isExistingDoctor ? 'bg-gray-100' : ''}
                />
                <Button 
                  type="button" 
                  onClick={handleSearchDoctor}
                  disabled={searchingDoctor || !doctorForm.registered_number || isExistingDoctor}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {searchingDoctor ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </div>

            {/* Show search result card if doctor found */}
            {searchedDoctor && (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-green-900">{searchedDoctor.name}</h4>
                      <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">Existing Doctor</span>
                    </div>
                    <p className="text-sm text-green-700">{searchedDoctor.specialization}</p>
                    <p className="text-xs text-green-600 mt-1">Doctor ID: {searchedDoctor.id}</p>
                    {searchedDoctor.hospital_ids && searchedDoctor.hospital_ids.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        Currently working at {searchedDoctor.hospital_ids.length} hospital(s)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="doctor_name">Name *</Label>
              <Input
                id="doctor_name"
                value={doctorForm.name}
                onChange={(e) => setDoctorForm({...doctorForm, name: e.target.value})}
                placeholder="e.g., Jane Smith"
                required
                readOnly={isExistingDoctor}
                disabled={isExistingDoctor}
                className={isExistingDoctor ? 'bg-gray-100' : ''}
              />
            </div>
            <div>
              <Label htmlFor="specialization">Specialization *</Label>
              <Select
                value={doctorForm.specialization}
                onValueChange={(value) => setDoctorForm({...doctorForm, specialization: value, super_specialization: ''})}
                disabled={isExistingDoctor}
                required
              >
                <SelectTrigger className={isExistingDoctor ? 'bg-gray-100' : ''}>
                  <SelectValue placeholder="Select specialization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General Medicine">General Medicine</SelectItem>
                  <SelectItem value="Pediatrician">Pediatrician</SelectItem>
                  <SelectItem value="General Surgeon">General Surgeon</SelectItem>
                  <SelectItem value="Neurosurgeon">Neurosurgeon</SelectItem>
                  <SelectItem value="Cardiologist">Cardiologist</SelectItem>
                  <SelectItem value="Neurologist">Neurologist</SelectItem>
                  <SelectItem value="Pulmonologist">Pulmonologist</SelectItem>
                  <SelectItem value="Gastroenterologist">Gastroenterologist</SelectItem>
                  <SelectItem value="Endocrinologist">Endocrinologist</SelectItem>
                  <SelectItem value="Dermatologist">Dermatologist</SelectItem>
                  <SelectItem value="ENT Specialist">ENT Specialist</SelectItem>
                  <SelectItem value="Dentist">Dentist</SelectItem>
                  <SelectItem value="Psychiatrist">Psychiatrist</SelectItem>
                  <SelectItem value="Obstetrician/Gynec">Obstetrician/Gynec</SelectItem>
                  <SelectItem value="Oncologist">Oncologist</SelectItem>
                  <SelectItem value="Urologist">Urologist</SelectItem>
                  <SelectItem value="Physiotherapy">Physiotherapy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Super Specialization - shown only if main specialization has options */}
            {doctorForm.specialization && SUPER_SPECIALIZATION_OPTIONS[doctorForm.specialization] && (
              <div>
                <Label htmlFor="super_specialization">Super Specialization</Label>
                <Select
                  value={doctorForm.super_specialization || "none"}
                  onValueChange={(value) => setDoctorForm({...doctorForm, super_specialization: value === "none" ? "" : value})}
                  disabled={isExistingDoctor}
                >
                  <SelectTrigger className={isExistingDoctor ? 'bg-gray-100' : ''}>
                    <SelectValue placeholder="Select super specialization (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {SUPER_SPECIALIZATION_OPTIONS[doctorForm.specialization].map((superSpec) => (
                      <SelectItem key={superSpec} value={superSpec}>{superSpec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Optional: Select a sub-specialty</p>
              </div>
            )}
            <div>
              <Label htmlFor="doctor_contact">Contact *</Label>
              <PhoneInput
                value={doctorForm.contact}
                onChange={(value) => setDoctorForm({...doctorForm, contact: value})}
                countryCode={doctorForm.country_code}
                onCountryCodeChange={(code) => setDoctorForm({...doctorForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
                disabled={isExistingDoctor}
              />
            </div>
            <div>
              <Label htmlFor="doctor_password">Password *</Label>
              <Input
                id="doctor_password"
                type="password"
                value={doctorForm.password}
                onChange={(e) => setDoctorForm({...doctorForm, password: e.target.value})}
                placeholder="Enter password for doctor login"
                required
                disabled={isExistingDoctor}
                className={isExistingDoctor ? 'bg-gray-100' : ''}
              />
            </div>

            <div>
              <Label htmlFor="doctor_photo">Photo (Optional)</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  id="doctor_photo"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedPhoto(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />
                <label htmlFor="doctor_photo" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload photo</p>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG (Max 5MB)</p>
                  </div>
                </label>
                {selectedPhoto && (
                  <p className="text-sm text-green-600 mt-2 text-center">
                    ✓ {selectedPhoto.name}
                  </p>
                )}
              </div>
            </div>
            
            {/* Multiple Hospital Selection */}
            <div>
              <Label>Assign to Additional Hospitals (Optional)</Label>
              
              {/* Show existing assignments for found doctors */}
              {searchedDoctor && searchedDoctor.hospital_ids && searchedDoctor.hospital_ids.length > 0 && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-medium text-blue-900 mb-2">Currently Assigned Hospitals:</p>
                  <div className="flex flex-wrap gap-2">
                    {searchedDoctor.hospital_ids.map(hId => {
                      const hospital = hospitals.find(h => h.id === hId);
                      return hospital ? (
                        <span key={hId} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {hospital.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mb-2">
                {searchedDoctor ? 'Add to more hospitals:' : `Primary: ${selectedHospitalForAdd?.name}`}
              </p>
              
              {/* Search Input */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search hospitals..."
                  value={hospitalSearchQuery}
                  onChange={(e) => setHospitalSearchQuery(e.target.value)}
                  className="pl-10 h-9 text-sm"
                />
              </div>
              
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                {hospitals
                  .filter(h => {
                    // For existing doctors, show all hospitals except already assigned ones
                    if (searchedDoctor) {
                      const alreadyAssigned = searchedDoctor.hospital_ids?.includes(h.id);
                      return !alreadyAssigned;
                    }
                    // For new doctors, exclude primary hospital
                    return h.id !== selectedHospitalForAdd?.id;
                  })
                  .filter(hospital => 
                    hospital.name.toLowerCase().includes(hospitalSearchQuery.toLowerCase()) ||
                    hospital.code.toLowerCase().includes(hospitalSearchQuery.toLowerCase())
                  )
                  .map(hospital => (
                  <label key={hospital.id} className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedHospitalsForDoctor.some(h => h.id === hospital.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedHospitalsForDoctor([...selectedHospitalsForDoctor, hospital]);
                        } else{
                          setSelectedHospitalsForDoctor(selectedHospitalsForDoctor.filter(h => h.id !== hospital.id));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{hospital.name} ({hospital.code})</span>
                  </label>
                ))}
                {hospitals
                  .filter(h => {
                    if (searchedDoctor) {
                      const alreadyAssigned = searchedDoctor.hospital_ids?.includes(h.id);
                      return !alreadyAssigned;
                    }
                    return h.id !== selectedHospitalForAdd?.id;
                  })
                  .filter(hospital => 
                    hospital.name.toLowerCase().includes(hospitalSearchQuery.toLowerCase()) ||
                    hospital.code.toLowerCase().includes(hospitalSearchQuery.toLowerCase())
                  ).length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-2">
                    {hospitalSearchQuery ? 'No hospitals found' : (searchedDoctor ? 'Already assigned to all hospitals' : 'No additional hospitals available')}
                  </p>
                )}
              </div>
              
              {selectedHospitalsForDoctor.length > 0 && (
                <p className="text-xs text-green-600 mt-2">
                  {searchedDoctor 
                    ? `✓ Doctor will be added to ${selectedHospitalsForDoctor.length} more hospital(s)`
                    : `✓ Doctor will work at ${selectedHospitalsForDoctor.length + 1} hospital(s) (including primary)`
                  }
                </p>
              )}
            </div>
            
            {doctorForm.registered_number && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
                <p className="font-medium">Doctor ID will be:</p>
                <p className="font-mono mt-1">
                  DOC{doctorForm.registered_number}
                </p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={!doctorForm.name || !doctorForm.specialization || !doctorForm.registered_number || !doctorForm.contact || uploadingPhoto}
              >
                {uploadingPhoto ? 'Uploading...' : 'Add Doctor'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowAddDoctorDialog(false);
                  setDoctorForm({ name: '', specialization: '', super_specialization: '', registered_number: '', contact: '', password: '', photo_url: '', country_code: '+91' });
                  setSelectedPhoto(null);
                  setSelectedHospitalForAdd(null);
                  setSelectedHospitalsForDoctor([]);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog open={showAddStaffDialog} onOpenChange={setShowAddStaffDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Front Desk Staff to {selectedHospitalForAdd?.name}</DialogTitle>
            <DialogDescription>
              Add a new front desk staff member to this hospital
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddStaff} className="space-y-4">
            <div>
              <Label htmlFor="staff_name">Name *</Label>
              <Input
                id="staff_name"
                value={staffForm.name}
                onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                placeholder="e.g., John Doe"
                required
              />
            </div>
            <div>
              <Label htmlFor="staff_phone">Phone Number *</Label>
              <PhoneInput
                value={staffForm.phone}
                onChange={(value) => setStaffForm({...staffForm, phone: value})}
                countryCode={staffForm.country_code}
                onCountryCodeChange={(code) => setStaffForm({...staffForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            <div>
              <Label htmlFor="staff_password">Password *</Label>
              <Input
                id="staff_password"
                type="password"
                value={staffForm.password}
                onChange={(e) => setStaffForm({...staffForm, password: e.target.value})}
                placeholder="Enter password for staff login"
                required
              />
            </div>
            <div>
              <Label htmlFor="staff_email">Email (Optional)</Label>
              <Input
                id="staff_email"
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({...staffForm, email: e.target.value})}
                placeholder="e.g., john@hospital.com"
              />
            </div>
            <div>
              <Label>Hospital</Label>
              <div className="p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm font-medium">{selectedHospitalForAdd?.name}</p>
              </div>
            </div>
            {selectedHospitalForAdd && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <p className="font-medium">Staff ID will be auto-generated:</p>
                <p className="font-mono mt-1">
                  FD{selectedHospitalForAdd.code.match(/\d{3}$/)?.[0] || '000'}XXX
                </p>
                <p className="text-xs mt-1 text-green-700">
                  (Last 3 digits from Hospital ID: {selectedHospitalForAdd.code})
                </p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                disabled={!staffForm.name || !staffForm.phone}
              >
                Add Staff
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowAddStaffDialog(false);
                  setStaffForm({ name: '', phone: '', email: '', country_code: '+91' });
                  setSelectedHospitalForAdd(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Medical Assistant Dialog */}
      <Dialog open={showAddMADialog} onOpenChange={(open) => {
        setShowAddMADialog(open);
        if (!open) {
          setEditingMA(null);
          setMAForm({ name: '', phone: '', email: '', password: '', doctor_ids: [], country_code: '+91' });
          setSelectedHospitalForAdd(null);
        }
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-ma-dialog">
          <DialogHeader>
            <DialogTitle>{editingMA ? 'Edit Medical Assistant' : `Add Medical Assistant to ${selectedHospitalForAdd?.name}`}</DialogTitle>
            <DialogDescription>
              {editingMA ? `Update details for ${editingMA.name} (${editingMA.id})` : 'Add a new medical assistant to this hospital'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingMA ? handleUpdateMA : handleAddMA} className="space-y-4">
            <div>
              <Label htmlFor="ma_name">Name *</Label>
              <Input
                id="ma_name"
                value={maForm.name}
                onChange={(e) => setMAForm({...maForm, name: e.target.value})}
                placeholder="e.g., Jane Smith"
                required
                data-testid="ma-name-input"
              />
            </div>
            <div>
              <Label htmlFor="ma_phone">Phone Number *</Label>
              <PhoneInput
                value={maForm.phone}
                onChange={(value) => setMAForm({...maForm, phone: value})}
                countryCode={maForm.country_code}
                onCountryCodeChange={(code) => setMAForm({...maForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            <div>
              <Label htmlFor="ma_password">{editingMA ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
              <Input
                id="ma_password"
                type="password"
                value={maForm.password}
                onChange={(e) => setMAForm({...maForm, password: e.target.value})}
                placeholder={editingMA ? "Enter new password (optional)" : "Enter password for MA login"}
                required={!editingMA}
                data-testid="ma-password-input"
              />
            </div>
            <div>
              <Label htmlFor="ma_email">Email (Optional)</Label>
              <Input
                id="ma_email"
                type="email"
                value={maForm.email}
                onChange={(e) => setMAForm({...maForm, email: e.target.value})}
                placeholder="e.g., jane@hospital.com"
              />
            </div>
            <div>
              <Label>Assign to Doctors *</Label>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                {doctors.filter(d => 
                  d.hospital_id === selectedHospitalForAdd?.id || 
                  (d.hospital_ids && d.hospital_ids.includes(selectedHospitalForAdd?.id))
                ).length === 0 ? (
                  <p className="text-sm text-gray-500">No doctors available. Add doctors first.</p>
                ) : (
                  doctors.filter(d => 
                    d.hospital_id === selectedHospitalForAdd?.id || 
                    (d.hospital_ids && d.hospital_ids.includes(selectedHospitalForAdd?.id))
                  ).map(doc => (
                    <label key={doc.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={maForm.doctor_ids.includes(doc.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMAForm({...maForm, doctor_ids: [...maForm.doctor_ids, doc.id]});
                          } else {
                            setMAForm({...maForm, doctor_ids: maForm.doctor_ids.filter(id => id !== doc.id)});
                          }
                        }}
                        className="w-4 h-4 text-pink-600 rounded border-gray-300"
                      />
                      <span className="text-sm">Dr. {doc.name}</span>
                      <span className="text-xs text-gray-400">({doc.specialization})</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Select doctors whose appointments this MA can view</p>
            </div>
            <div>
              <Label>Hospital</Label>
              <div className="p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm font-medium">{selectedHospitalForAdd?.name}</p>
              </div>
            </div>
            {!editingMA && selectedHospitalForAdd && (
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 text-sm text-pink-800">
                <p className="font-medium">Staff ID will be auto-generated:</p>
                <p className="font-mono mt-1">
                  MA{selectedHospitalForAdd.code.match(/\d{3}$/)?.[0] || '000'}XXX
                </p>
                <p className="text-xs mt-1 text-pink-700">
                  (Last 3 digits from Hospital ID: {selectedHospitalForAdd.code})
                </p>
              </div>
            )}
            {editingMA && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">Staff ID: <span className="font-mono">{editingMA.id}</span></p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                className="flex-1 bg-pink-600 hover:bg-pink-700"
                disabled={!maForm.name || !maForm.phone || (!editingMA && !maForm.password)}
                data-testid="save-ma-button"
              >
                {editingMA ? 'Update Medical Assistant' : 'Add Medical Assistant'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowAddMADialog(false);
                  setEditingMA(null);
                  setMAForm({ name: '', phone: '', email: '', password: '', doctor_ids: [], country_code: '+91' });
                  setSelectedHospitalForAdd(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Doctor Dialog */}
      <Dialog open={showEditDoctorDialog} onOpenChange={setShowEditDoctorDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Doctor</DialogTitle>
            <DialogDescription>
              Update doctor information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateDoctor} className="space-y-4">
            <div>
              <Label htmlFor="edit_doctor_name">Name *</Label>
              <Input
                id="edit_doctor_name"
                value={doctorForm.name}
                onChange={(e) => setDoctorForm({...doctorForm, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_specialization">Specialization *</Label>
              <Select
                value={doctorForm.specialization}
                onValueChange={(value) => setDoctorForm({...doctorForm, specialization: value, super_specialization: ''})}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select specialization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General Medicine">General Medicine</SelectItem>
                  <SelectItem value="Pediatrician">Pediatrician</SelectItem>
                  <SelectItem value="General Surgeon">General Surgeon</SelectItem>
                  <SelectItem value="Neurosurgeon">Neurosurgeon</SelectItem>
                  <SelectItem value="Cardiologist">Cardiologist</SelectItem>
                  <SelectItem value="Neurologist">Neurologist</SelectItem>
                  <SelectItem value="Pulmonologist">Pulmonologist</SelectItem>
                  <SelectItem value="Gastroenterologist">Gastroenterologist</SelectItem>
                  <SelectItem value="Endocrinologist">Endocrinologist</SelectItem>
                  <SelectItem value="Dermatologist">Dermatologist</SelectItem>
                  <SelectItem value="ENT Specialist">ENT Specialist</SelectItem>
                  <SelectItem value="Dentist">Dentist</SelectItem>
                  <SelectItem value="Psychiatrist">Psychiatrist</SelectItem>
                  <SelectItem value="Obstetrician/Gynec">Obstetrician/Gynec</SelectItem>
                  <SelectItem value="Oncologist">Oncologist</SelectItem>
                  <SelectItem value="Urologist">Urologist</SelectItem>
                  <SelectItem value="Physiotherapy">Physiotherapy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Super Specialization - shown only if main specialization has options */}
            {doctorForm.specialization && SUPER_SPECIALIZATION_OPTIONS[doctorForm.specialization] && (
              <div>
                <Label htmlFor="edit_super_specialization">Super Specialization</Label>
                <Select
                  value={doctorForm.super_specialization || "none"}
                  onValueChange={(value) => setDoctorForm({...doctorForm, super_specialization: value === "none" ? "" : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select super specialization (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {SUPER_SPECIALIZATION_OPTIONS[doctorForm.specialization].map((superSpec) => (
                      <SelectItem key={superSpec} value={superSpec}>{superSpec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Optional: Select a sub-specialty</p>
              </div>
            )}
            <div>
              <Label htmlFor="edit_registered_number">Registered Number</Label>
              <Input
                id="edit_registered_number"
                value={doctorForm.registered_number}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Registered number cannot be changed</p>
            </div>
            <div>
              <Label htmlFor="edit_doctor_contact">Contact *</Label>
              <PhoneInput
                value={doctorForm.contact}
                onChange={(value) => setDoctorForm({...doctorForm, contact: value})}
                countryCode={doctorForm.country_code}
                onCountryCodeChange={(code) => setDoctorForm({...doctorForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_doctor_photo">Photo</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  id="edit_doctor_photo"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedPhoto(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />
                <label htmlFor="edit_doctor_photo" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload photo</p>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG (Max 5MB)</p>
                  </div>
                </label>
                {selectedPhoto && (
                  <p className="text-sm text-green-600 mt-2 text-center">
                    ✓ {selectedPhoto.name}
                  </p>
                )}
                {doctorForm.photo_url && !selectedPhoto && (
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    Current photo: {doctorForm.photo_url.split('/').pop()}
                  </p>
                )}
              </div>
            </div>
            
            {/* Hospital Assignment Section */}
            <div>
              <Label>Assign to Hospitals</Label>
              <p className="text-xs text-gray-500 mb-2">Select all hospitals where this doctor works</p>
              
              {/* Search Input */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search hospitals..."
                  value={hospitalSearchQuery}
                  onChange={(e) => setHospitalSearchQuery(e.target.value)}
                  className="pl-10 h-9 text-sm"
                />
              </div>
              
              {/* Hospital List */}
              <div className="max-h-60 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                {hospitals
                  .filter(hospital => 
                    hospital.name.toLowerCase().includes(hospitalSearchQuery.toLowerCase()) ||
                    hospital.code.toLowerCase().includes(hospitalSearchQuery.toLowerCase())
                  )
                  .map(hospital => (
                    <label key={hospital.id} className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedHospitalsForDoctor.some(h => h.id === hospital.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedHospitalsForDoctor([...selectedHospitalsForDoctor, hospital]);
                          } else {
                            setSelectedHospitalsForDoctor(selectedHospitalsForDoctor.filter(h => h.id !== hospital.id));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{hospital.name} ({hospital.code})</span>
                    </label>
                  ))
                }
                {hospitals.filter(hospital => 
                  hospital.name.toLowerCase().includes(hospitalSearchQuery.toLowerCase()) ||
                  hospital.code.toLowerCase().includes(hospitalSearchQuery.toLowerCase())
                ).length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-2">
                    {hospitalSearchQuery ? 'No hospitals found' : 'No hospitals available'}
                  </p>
                )}
              </div>
              
              {selectedHospitalsForDoctor.length > 0 && (
                <p className="text-xs text-green-600 mt-2">
                  ✓ Doctor will work at {selectedHospitalsForDoctor.length} hospital(s)
                </p>
              )}
              {selectedHospitalsForDoctor.length === 0 && (
                <p className="text-xs text-red-600 mt-2">
                  ⚠️ Please select at least one hospital
                </p>
              )}
            </div>
            
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={!doctorForm.name || !doctorForm.specialization || !doctorForm.contact || uploadingPhoto || selectedHospitalsForDoctor.length === 0}
              >
                {uploadingPhoto ? 'Uploading...' : 'Update Doctor'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditDoctorDialog(false);
                  setEditingDoctor(null);
                  setDoctorForm({ name: '', specialization: '', super_specialization: '', registered_number: '', contact: '', password: '', photo_url: '', country_code: '+91' });
                  setSelectedPhoto(null);
                  setSelectedHospitalsForDoctor([]);
                  setHospitalSearchQuery('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={showEditStaffDialog} onOpenChange={setShowEditStaffDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Front Desk Staff</DialogTitle>
            <DialogDescription>
              Update front desk staff information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateStaff} className="space-y-4">
            <div>
              <Label htmlFor="edit_staff_name">Name *</Label>
              <Input
                id="edit_staff_name"
                value={staffForm.name}
                onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_staff_phone">Phone Number *</Label>
              <PhoneInput
                value={staffForm.phone}
                onChange={(value) => setStaffForm({...staffForm, phone: value})}
                countryCode={staffForm.country_code}
                onCountryCodeChange={(code) => setStaffForm({...staffForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_staff_email">Email (Optional)</Label>
              <Input
                id="edit_staff_email"
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({...staffForm, email: e.target.value})}
              />
            </div>
            <div>
              <Label>Hospital</Label>
              <div className="p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm font-medium">{editingStaff ? getHospitalName(editingStaff.hospital_id) : ''}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={!staffForm.name || !staffForm.phone}
              >
                Update Staff
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditStaffDialog(false);
                  setEditingStaff(null);
                  setStaffForm({ name: '', phone: '', email: '', country_code: '+91' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Baby Dialog */}
      <Dialog open={showEditBabyDialog} onOpenChange={setShowEditBabyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Information</DialogTitle>
            <DialogDescription>
              Update profile information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateBaby} className="space-y-4">
            <div>
              <Label htmlFor="baby_name">Baby Name *</Label>
              <Input
                id="baby_name"
                value={babyForm.name}
                onChange={(e) => setBabyForm({...babyForm, name: e.target.value})}
                placeholder="Enter baby's name"
                required
              />
            </div>
            <div>
              <Label htmlFor="baby_dob">Date of Birth *</Label>
              <DateInput
                id="baby_dob"
                value={babyForm.date_of_birth}
                onChange={(e) => setBabyForm({...babyForm, date_of_birth: e.target.value})}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Format: DD/MM/YYYY</p>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={!babyForm.name || !babyForm.date_of_birth}
              >
                Update Baby
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditBabyDialog(false);
                  setEditingBaby(null);
                  setBabyForm({ name: '', date_of_birth: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Baby Dialog - First Confirmation */}
      <Dialog open={showDeleteBabyDialog} onOpenChange={setShowDeleteBabyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete</DialogTitle>
            <DialogDescription>
              This action cannot be undone
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-semibold">{deletingBaby?.name}</span>?
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Central Admin Override:</strong> This will delete the baby and ALL associated records including medical sessions, appointments, memories, and other data.
              </p>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={handleDeleteBabyFirstStep}
                variant="destructive"
                className="flex-1"
              >
                Continue
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancelDelete}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Baby Dialog - Second Confirmation */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 font-medium">⚠️ PERMANENT DELETION WARNING</p>
              <p className="text-red-700 text-sm mt-1">
                You are about to permanently delete <strong>{deletingBaby?.name}</strong> and ALL associated data including:
              </p>
              <ul className="text-red-700 text-sm mt-2 list-disc list-inside">
                <li>Medical sessions and records</li>
                <li>Vaccinations and growth records</li>
                <li>Appointments and memories</li>
                <li>All other baby-related data</li>
              </ul>
            </div>
            <p className="text-gray-700">
              This action cannot be undone. Please type <span className="font-mono font-bold bg-gray-100 px-2 py-1 rounded">DELETE</span> to confirm.
            </p>
            <div>
              <Label htmlFor="delete_confirm">Type DELETE to confirm</Label>
              <Input
                id="delete_confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="mt-2"
              />
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={handleDeleteBabyConfirm}
                variant="destructive"
                className="flex-1"
                disabled={deleteConfirmText !== 'DELETE'}
              >
                Delete & All Records
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancelDelete}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>


      {/* Delete Doctor Dialog - First Confirmation */}
      <Dialog open={showDeleteDoctorDialog} onOpenChange={setShowDeleteDoctorDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Doctor</DialogTitle>
            <DialogDescription>
              This action cannot be undone
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-semibold">Dr. {deletingDoctor?.name}</span>?
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> This will permanently remove the doctor from the system. All medical sessions, appointments, and doctor-related data will be affected.
              </p>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={handleDeleteDoctorFirstStep}
                variant="destructive"
                className="flex-1"
              >
                Continue
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancelDeleteDoctor}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Doctor Dialog - Second Confirmation */}
      <Dialog open={showDeleteDoctorConfirmDialog} onOpenChange={setShowDeleteDoctorConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Doctor Deletion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 font-medium">⚠️ PERMANENT DELETION WARNING</p>
              <p className="text-red-700 text-sm mt-1">
                You are about to permanently delete <strong>Dr. {deletingDoctor?.name}</strong> (ID: {deletingDoctor?.id}) from the system.
              </p>
            </div>
            <p className="text-gray-700">
              This action cannot be undone. Please type <span className="font-mono font-bold bg-gray-100 px-2 py-1 rounded">DELETE</span> to confirm.
            </p>
            <div>
              <Label htmlFor="delete_doctor_confirm">Type DELETE to confirm</Label>
              <Input
                id="delete_doctor_confirm"
                value={deleteDoctorConfirmText}
                onChange={(e) => setDeleteDoctorConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="mt-2"
              />
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={handleDeleteDoctorConfirm}
                variant="destructive"
                className="flex-1"
                disabled={deleteDoctorConfirmText !== 'DELETE'}
              >
                Delete Doctor
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancelDeleteDoctor}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      </Dialog>

      {/* Bulk Delete Dialog - First Confirmation */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Multiple Records</DialogTitle>
            <DialogDescription>
              This action cannot be undone
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-semibold">{selectedBabies.size} babies</span>?
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Central Admin Override:</strong> This will delete all selected babies and ALL their associated records including medical sessions, appointments, memories, and other data.
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-sm font-medium text-gray-700 mb-2">Selected babies:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                {Array.from(selectedBabies).map(babyId => {
                  const baby = babies.find(b => b.id === babyId);
                  return baby ? (
                    <li key={babyId}>• {baby.name} ({baby.short_id || baby.id.substring(0, 8).toUpperCase()})</li>
                  ) : null;
                })}
              </ul>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={handleBulkDeleteFirstStep}
                variant="destructive"
                className="flex-1"
              >
                Continue
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancelBulkDelete}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog - Second Confirmation */}
      <Dialog open={showBulkDeleteConfirmDialog} onOpenChange={setShowBulkDeleteConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Bulk Deletion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 font-medium">⚠️ PERMANENT BULK DELETION WARNING</p>
              <p className="text-red-700 text-sm mt-1">
                You are about to permanently delete <strong>{selectedBabies.size} babies</strong> and ALL their associated data including:
              </p>
              <ul className="text-red-700 text-sm mt-2 list-disc list-inside">
                <li>Medical sessions and records</li>
                <li>Vaccinations and growth records</li>
                <li>Appointments and memories</li>
                <li>All other baby-related data</li>
              </ul>
            </div>
            <p className="text-gray-700">
              This action cannot be undone. Please type <span className="font-mono font-bold bg-gray-100 px-2 py-1 rounded">DELETE</span> to confirm.
            </p>
            <div>
              <Label htmlFor="bulk_delete_confirm">Type DELETE to confirm</Label>
              <Input
                id="bulk_delete_confirm"
                value={bulkDeleteConfirmText}
                onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="mt-2"
              />
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={handleBulkDeleteConfirm}
                variant="destructive"
                className="flex-1"
                disabled={bulkDeleteConfirmText !== 'DELETE'}
              >
                Delete {selectedBabies.size} Babies & All Records
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancelBulkDelete}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Common Medications Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Common Medications</DialogTitle>
            <DialogDescription>
              Import multiple common medications from a CSV file
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>CSV Format:</strong> Upload a CSV file with medications that all doctors can use.
              </p>
            </div>

            {/* Download Template Button */}
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const csvContent = `brand_name,generic_name,medication_type,category,treatment_for
Crocin 650mg,Paracetamol,tablet,Analgesic,Fever and Pain Relief
Augmentin 625mg,Amoxicillin + Clavulanic Acid,tablet,Antibiotic,Bacterial Infections
Allegra 120mg,Fexofenadine,tablet,Antihistamine,Allergic Rhinitis
Metformin 500mg,Metformin,tablet,Antidiabetic,Type 2 Diabetes
Pantop 40mg,Pantoprazole,tablet,Proton Pump Inhibitor,Acidity and GERD`;
                  
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = 'medications_template.csv';
                  link.click();
                }}
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Sample CSV Template
              </Button>
            </div>

            <div>
              <Label className="text-sm font-medium">Required CSV Columns (Header Row)</Label>
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-3">
                <p className="text-xs font-mono text-gray-700 break-all">
                  brand_name,generic_name,medication_type,category,treatment_for
                </p>
              </div>
              <ul className="text-xs text-gray-600 mt-2 space-y-1 list-disc list-inside">
                <li><strong className="text-red-600">brand_name</strong>: Required (e.g., "Crocin 650mg")</li>
                <li><strong className="text-red-600">generic_name</strong>: Required (e.g., "Paracetamol")</li>
                <li><strong>medication_type</strong>: Optional (tablet/capsule/syrup/injection)</li>
                <li><strong>category</strong>: Optional (e.g., "Analgesic", "Antibiotic")</li>
                <li><strong>treatment_for</strong>: Optional (e.g., "Fever and Pain Relief")</li>
              </ul>
            </div>

            <div>
              <Label className="text-sm font-medium">Select CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                className="mt-2"
              />
              {csvFile && (
                <p className="text-sm text-green-600 mt-2 flex items-center">
                  <span className="mr-2">✓</span> Selected: {csvFile.name}
                </p>
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                <strong>💡 Tips:</strong>
              </p>
              <ul className="text-xs text-yellow-700 mt-1 space-y-1 list-disc list-inside">
                <li>Save Excel file as "CSV UTF-8 (Comma delimited) (*.csv)"</li>
                <li>First row must be the header: brand_name,generic_name,medication_type,category,treatment_for</li>
                <li><strong>Both brand_name and generic_name are mandatory</strong></li>
                <li>Duplicate brand names will be skipped automatically</li>
                <li>Leave optional fields empty if not needed</li>
              </ul>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button 
                onClick={handleBulkImportCSV}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={!csvFile || uploadingCSV}
              >
                {uploadingCSV ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading & Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Medications
                  </>
                )}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowBulkImportDialog(false);
                  setCsvFile(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Medication Dialog - First Confirmation */}
      <Dialog open={showDeleteMedicationDialog} onOpenChange={setShowDeleteMedicationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Medication</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this medication from the common medications database?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {medicationToDelete && (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="font-medium text-gray-900">{medicationToDelete.medication_name}</p>
                {medicationToDelete.generic_name && (
                  <p className="text-sm text-gray-600">Generic: {medicationToDelete.generic_name}</p>
                )}
                {medicationToDelete.category && (
                  <p className="text-sm text-gray-600">Category: {medicationToDelete.category}</p>
                )}
              </div>
            )}
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Important:</strong> This will only delete the medication from the common database. 
                Any doctor's personal medication templates or prescriptions that reference this medication will continue to work with the medication name.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                onClick={handleCancelDeleteMedication}
                variant="outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteMedicationFirstStep}
                variant="destructive"
              >
                Continue to Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Medication Dialog - Second Confirmation */}
      <Dialog open={showDeleteMedicationConfirmDialog} onOpenChange={setShowDeleteMedicationConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Final Confirmation Required</DialogTitle>
            <DialogDescription>
              You are about to permanently delete this medication.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {medicationToDelete && (
              <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4">
                <p className="text-red-800 font-semibold mb-2">You are about to delete:</p>
                <p className="font-medium text-gray-900">{medicationToDelete.medication_name}</p>
                {medicationToDelete.generic_name && (
                  <p className="text-sm text-gray-600">Generic: {medicationToDelete.generic_name}</p>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="confirmDeleteMedication" className="text-sm font-medium text-gray-700">
                Type <span className="font-bold text-red-600">DELETE</span> to confirm
              </Label>
              <Input
                id="confirmDeleteMedication"
                value={deleteMedicationConfirmText}
                onChange={(e) => setDeleteMedicationConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="mt-2"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                onClick={handleCancelDeleteMedication}
                variant="outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteMedicationConfirm}
                variant="destructive"
                disabled={deleteMedicationConfirmText !== 'DELETE'}
              >
                Permanently Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Bulk Delete Medications Dialog - First Confirmation */}
      <Dialog open={showBulkDeleteMedicationDialog} onOpenChange={setShowBulkDeleteMedicationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Multiple Medications</DialogTitle>
            <DialogDescription>
              This action cannot be undone
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-semibold">{selectedMedications.size} selected medications</span>?
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> This will delete the selected medications from the common database. Any doctor's personal medication templates or prescriptions that reference these medications will continue to work with the medication names.
              </p>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={handleBulkDeleteMedicationsFirstStep}
                variant="destructive"
                className="flex-1"
              >
                Continue
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancelBulkDeleteMedications}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Medications Dialog - Second Confirmation */}
      <Dialog open={showBulkDeleteMedicationConfirmDialog} onOpenChange={setShowBulkDeleteMedicationConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 font-medium">⚠️ PERMANENT DELETION WARNING</p>
              <p className="text-red-700 text-sm mt-1">
                You are about to permanently delete <strong>{selectedMedications.size} medications</strong> from the common database.
              </p>
            </div>
            <p className="text-gray-700">
              This action cannot be undone. Please type <span className="font-mono font-bold bg-gray-100 px-2 py-1 rounded">DELETE</span> to confirm.
            </p>
            <div>
              <Label htmlFor="bulk_delete_medications_confirm">Type DELETE to confirm</Label>
              <Input
                id="bulk_delete_medications_confirm"
                value={bulkDeleteMedicationConfirmText}
                onChange={(e) => setBulkDeleteMedicationConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="mt-2"
              />
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={handleBulkDeleteMedicationsConfirm}
                variant="destructive"
                className="flex-1"
                disabled={bulkDeleteMedicationConfirmText !== 'DELETE'}
              >
                Delete Medications
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancelBulkDeleteMedications}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Delete All dialogs removed */}



      {/* Add Test Dialog */}
      <Dialog open={showAddTestDialog} onOpenChange={setShowAddTestDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Common Test</DialogTitle>
            <DialogDescription>
              Add a test that will be available as a default template for all doctors
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddTest} className="space-y-4">
            <div>
              <Label htmlFor="test_name">Test Name *</Label>
              <Input
                id="test_name"
                value={testForm.test_name}
                onChange={(e) => setTestForm({...testForm, test_name: e.target.value})}
                placeholder="e.g., Complete Blood Count (CBC)"
                required
              />
            </div>
            <div>
              <Label htmlFor="test_type">Test Type *</Label>
              <select
                id="test_type"
                value={testForm.test_type}
                onChange={(e) => setTestForm({...testForm, test_type: e.target.value, category: ''})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Blood Test">Blood Test</option>
                <option value="Urine Test">Urine Test</option>
                <option value="Imaging">Imaging</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {testForm.test_type === 'Blood Test' && (
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={testForm.category}
                  onChange={(e) => setTestForm({...testForm, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Category</option>
                  <option value="Diabetic Screen">Diabetic Screen</option>
                  <option value="Heart Screen">Heart Screen</option>
                  <option value="Liver Screen">Liver Screen</option>
                  <option value="Kidney Screen">Kidney Screen</option>
                  <option value="Minerals">Minerals</option>
                  <option value="Hormones Screen">Hormones Screen</option>
                  <option value="Vitamins">Vitamins</option>
                  <option value="Anemia & Inflammatory Screen">Anemia & Inflammatory Screen</option>
                  <option value="General Body Screen">General Body Screen</option>
                </select>
              </div>
            )}
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={testForm.description}
                onChange={(e) => setTestForm({...testForm, description: e.target.value})}
                placeholder="Additional notes about this test"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                type="button"
                onClick={() => {
                  setShowAddTestDialog(false);
                  setTestForm({
                    test_name: '',
                    test_type: 'Blood Test',
                    category: '',
                    description: ''
                  });
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                Add Test
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Test Dialog */}
      <Dialog open={showDeleteTestDialog} onOpenChange={setShowDeleteTestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Test</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this test from the common tests database?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {testToDelete && (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="font-medium text-gray-900">{testToDelete.test_name}</p>
                <p className="text-sm text-gray-600">Type: {testToDelete.test_type}</p>
                {testToDelete.category && (
                  <p className="text-sm text-gray-600">Category: {testToDelete.category}</p>
                )}
              </div>
            )}
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Important:</strong> This will remove the test from the common database and it will no longer appear as a default template for doctors. 
                Doctors can still add this test manually if needed.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                onClick={() => {
                  setShowDeleteTestDialog(false);
                  setTestToDelete(null);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteTest}
                variant="destructive"
              >
                Delete Test
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Common Medication Dialog */}
      <Dialog open={showAddMedicationDialog} onOpenChange={setShowAddMedicationDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Common Medication</DialogTitle>
            <DialogDescription>
              Add a medication template that will be available for all doctors
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCommonMedication} className="space-y-4">
            <div>
              <Label>Medication Type (Optional)</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {['injection', 'pill', 'syrup', 'tablet'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setMedicationForm(prev => ({ ...prev, medication_type: type }))}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:bg-gray-50 ${
                      medicationForm.medication_type === type
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <MedicationIcon type={type} size={24} className={`mb-1 ${
                      medicationForm.medication_type === type ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span className={`text-xs capitalize ${
                      medicationForm.medication_type === type ? 'text-blue-600 font-medium' : 'text-gray-500'
                    }`}>
                      {type}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Medication Name *</Label>
              <Input
                placeholder="e.g., Paracetamol 500mg"
                value={medicationForm.medication_name}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, medication_name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>Generic Name *</Label>
              <Input
                placeholder="e.g., Acetaminophen"
                value={medicationForm.generic_name}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, generic_name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>Manufacturer</Label>
              <Input
                placeholder="e.g., Cipla, Sun Pharma, Dr. Reddy's"
                value={medicationForm.manufacturer}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, manufacturer: e.target.value }))}
              />
            </div>

            <div>
              <Label>Category</Label>
              <Input
                placeholder="e.g., Analgesic, Antibiotic, Antihistamine"
                value={medicationForm.category}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, category: e.target.value }))}
              />
            </div>

            <div>
              <Label>Treatment For</Label>
              <Input
                placeholder="e.g., Fever, Pain, Infection"
                value={medicationForm.treatment_for}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, treatment_for: e.target.value }))}
              />
            </div>

            <div>
              <Label>Time of Day & Frequency</Label>
              
              {/* Time of Day */}
              <div className="flex items-center gap-3 mt-2 ml-1">
                <span className="text-xs text-gray-600 w-20">Time:</span>
                {['Morning', 'Afternoon', 'Evening', 'Night'].map(time => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => toggleTimeOfDay(time)}
                    className="flex flex-col items-center gap-1 group"
                  >
                    <div className={`w-2.5 h-2.5 rounded-full transition-all ${
                      medicationForm.time_of_day.includes(time) 
                        ? 'bg-purple-600 shadow-sm' 
                        : 'bg-gray-300 group-hover:bg-purple-400'
                    }`} />
                    <span className={`text-[10px] transition-all ${
                      medicationForm.time_of_day.includes(time)
                        ? 'text-purple-600 font-medium'
                        : 'text-gray-500 group-hover:text-purple-500'
                    }`}>
                      {time.substring(0, 1)}
                    </span>
                  </button>
                ))}
              </div>
              
              {/* Frequency Buttons */}
              <div className="flex items-center gap-2 mt-2 ml-1">
                <span className="text-xs text-gray-600 w-20">Frequency:</span>
                {['Once a day', 'Once a week', 'Once a month'].map((freq) => {
                  const isSelected = medicationForm.frequency === freq;
                  return (
                    <button
                      key={freq}
                      type="button"
                      onClick={() => {
                        setMedicationForm(prev => ({ ...prev, frequency: isSelected ? '' : freq }));
                      }}
                      className={`px-2 py-1 text-[10px] rounded transition-all ${
                        isSelected
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-200 text-gray-600 hover:bg-purple-100'
                      }`}
                    >
                      {freq}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Timing *</Label>
              <Select value={medicationForm.timing} onValueChange={(value) => setMedicationForm(prev => ({ ...prev, timing: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Before Food">Before Food</SelectItem>
                  <SelectItem value="After Food">After Food</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Duration</Label>
              <Input
                placeholder="e.g., 7 days, 2 weeks"
                value={medicationForm.duration}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, duration: e.target.value }))}
              />
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Additional instructions..."
                value={medicationForm.notes}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Medication
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowAddMedicationDialog(false);
                  setMedicationForm({
                    medication_name: '',
                    medication_type: '',
                    generic_name: '',
                    treatment_for: '',
                    time_of_day: [],
                    frequency: '',
                    timing: 'Before Food',
                    duration: '',
                    notes: ''
                  });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Database Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Database Backup</DialogTitle>
            <DialogDescription>
              Upload a JSON backup file to restore data. This is useful for migrating data between databases.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="import_file">Backup File (JSON)</Label>
              <Input
                id="import_file"
                type="file"
                accept=".json"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="mt-1"
              />
              {importFile && (
                <p className="text-sm text-green-600 mt-1">
                  Selected: {importFile.name}
                </p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                id="clear_existing"
                type="checkbox"
                checked={clearBeforeImport}
                onChange={(e) => setClearBeforeImport(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="clear_existing" className="text-sm text-gray-700">
                Clear existing data before import
              </Label>
            </div>
            
            {clearBeforeImport && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Warning:</strong> This will delete all existing data in the collections before importing. This action cannot be undone.
                </p>
              </div>
            )}
            
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 <strong>Tip:</strong> Use "Export" on your production environment to download the backup, then "Import" here to restore it.
              </p>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <Button
                onClick={handleImportDatabase}
                disabled={!importFile || importing}
                className="flex-1"
              >
                {importing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Data
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                  setClearBeforeImport(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CentralAdminDashboard;
